import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { ModernButton } from '../ui/ModernButton';
import { dbService } from '../../services/DatabaseService';
import fileUploadService from '../../services/FileUploadService';
import { sanitizeRichText } from '../../utils/helpers';
import WithdrawalModal from '../modals/WithdrawalModal';
import { normalizeQuestionnaireData, computeDisplayLabels } from '../../utils/questionnairePages';
import { NAME_QUESTION_SEED } from '../../utils/questionnaireEditorConstants';
import { isConditionalSatisfied } from '../../utils/conditionalVisibility';

// Default questions that always show before custom questions
// Default questions are now loaded from the questionnaire data

const DEFAULT_SECTIONS_TEXT = 'Rahmendaten & Qualifikationen: Grundlegende Informationen über Ihre Ausbildung, Erfahrung und Verfügbarkeit\nSpezifische Fragen: Position-spezifische Fragen zu Ihren Fähigkeiten und Erfahrungen\nÜberprüfung: Überprüfung Ihrer Antworten vor der finalen Einreichung';

// Session-unique row id. Used as React key for dynamic matrix/combobox rows so
// reconciliation cannot bind DOM state (focus, cursor, IME state) to the wrong
// row after a delete or reorder. Only used as a key; never read by validation
// or scoring, so old submissions without `id` still work.
let __rowIdCounter = 0;
const makeRowId = () => `r-${Date.now().toString(36)}-${(__rowIdCounter++).toString(36)}`;

// Matrix tables don't fit on phone screens; below this width each matrix row
// renders as a stacked card instead. JS-driven (matchMedia) because the
// prebuilt output.css lacks the responsive utility classes this would need.
const MOBILE_MEDIA_QUERY = '(max-width: 640px)';
function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

const stripHtmlTags = (text = '') => text.replace(/<[^>]*>/g, '').trim();

const getSectionLabelFromLine = (line, fallback) => {
  const cleanLine = stripHtmlTags(line || '');
  if (!cleanLine) return fallback;

  const separatorIndex = cleanLine.indexOf(':');
  if (separatorIndex === -1) return cleanLine;

  const title = cleanLine.slice(0, separatorIndex).trim();
  return title || fallback;
};

const getSectionDescriptionFromLine = (line, fallback = '') => {
  const cleanLine = stripHtmlTags(line || '');
  if (!cleanLine) return fallback;

  const separatorIndex = cleanLine.indexOf(':');
  if (separatorIndex === -1) return fallback;

  const description = cleanLine.slice(separatorIndex + 1).trim();
  return description || fallback;
};

const clampDefaultQuestionsPageOrder = (order, customPageCount) => {
  const maxOrder = Math.max(1, Number(customPageCount) + 1);
  const numericOrder = Number(order);
  if (!Number.isFinite(numericOrder)) return maxOrder;
  return Math.min(Math.max(Math.round(numericOrder), 1), maxOrder);
};

// Standalone theme toggle for applicant forms (no admin access)
function StandaloneThemeToggle({ theme, setTheme }) {
  const isDark = theme === 'dark';
  return (
    <button
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="fixed top-4 right-4 z-50 group p-2 rounded-full border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-700/80 backdrop-blur shadow hover:shadow-md hover:ring-2 ring-slate-200 dark:ring-slate-600 transition"
    >
      <span className="relative block h-6 w-6">
        <svg 
          className={`${isDark ? 'opacity-0 scale-75 -rotate-90' : 'opacity-100 scale-100 rotate-0'} text-amber-500 drop-shadow-xs transition-all duration-300 ease-out absolute inset-0`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" fill="currentColor" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </g>
        </svg>
        <svg 
          className={`${isDark ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-75 rotate-90'} text-slate-600 dark:text-slate-400 transition-all duration-300 ease-out absolute inset-0`}
          viewBox="0 0 24 24"
        >
          <path fill="currentColor" d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
        </svg>
      </span>
    </button>
  );
}

// Standalone Applicant Form (Security Isolated)
export function StandaloneApplicantForm({ questionnaireId }) {
  // Helper function to count characters in text
  const getCharacterCount = (text) => {
    if (!text || typeof text !== 'string') return 0;
    // Remove HTML tags and count characters
    const cleanText = text.replace(/<[^>]*>/g, '').trim();
    return cleanText.length;
  };

  const [questionnaire, setQuestionnaire] = useState(null);
  const isMobileViewport = useIsMobileViewport();
  const [isQuestionnaireLoading, setIsQuestionnaireLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [invalidAccessToken, setInvalidAccessToken] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [unansweredModalState, setUnansweredModalState] = useState({
    isOpen: false,
    questions: []
  });
  const [isWithdrawn, setIsWithdrawn] = useState(false);
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(`form_answers_${questionnaireId}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Corrupted localStorage data for answers, resetting:', error);
      // Clear corrupted data to prevent future issues
      try {
        localStorage.removeItem(`form_answers_${questionnaireId}`);
      } catch (clearError) {
        console.warn('Failed to clear corrupted localStorage data:', clearError);
      }
    }
    return {};
  });
  const applicantName = useMemo(() => {
    const nameFromAnswer = answers['system_name'];
    if (typeof nameFromAnswer === 'string' && nameFromAnswer.trim()) return nameFromAnswer;
    try {
      const legacy = localStorage.getItem(`form_name_${questionnaireId}`);
      return legacy || '';
    } catch {
      return '';
    }
  }, [answers, questionnaireId]);
  const [datenschutzConsent, setDatenschutzConsent] = useState(() => {
    try {
      const saved = localStorage.getItem(`form_datenschutz_${questionnaireId}`);
      return saved === 'true';
    } catch (error) {
      console.warn('Failed to load datenschutz consent from localStorage:', error);
      return false;
    }
  });
  const [showDatenschutzError, setShowDatenschutzError] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const saved = localStorage.getItem(`form_step_${questionnaireId}`);
      const timerStarted = localStorage.getItem(`timer_started_${questionnaireId}`) === 'true';
      
      // If timer was started, don't go back to intro
      if (timerStarted && saved === 'intro') {
        return 'default';
      }
      
      return saved || 'intro';
    } catch (error) {
      console.warn('Failed to load current step from localStorage:', error);
      return 'intro';
    }
  }); // 'intro', 'default', 'custom', 'confirm'
  const [customPageIndex, setCustomPageIndex] = useState(0);
  const hasAppliedInitialQPageRef = useRef(false);
  const [showConfirmForm, setShowConfirmForm] = useState(false);
  const [timerStarted, setTimerStarted] = useState(() => {
    try {
      const saved = localStorage.getItem(`timer_started_${questionnaireId}`);
      return saved === 'true';
    } catch (error) {
      console.warn('Failed to load timer state from localStorage:', error);
      return false;
    }
  });
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds (for tracking, not displayed)
  const [startTime, setStartTime] = useState(null);
  const timerIntervalRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const handleAutoSubmitRef = useRef(null);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
    return 'light';
  });

  // Initialize consolidated input log object
  const [inputLog, setInputLog] = useState(() => {
    try {
      const saved = localStorage.getItem(`input_log_${questionnaireId}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Corrupted localStorage data for input log, resetting:', error);
      // Clear corrupted data to prevent future issues
      try {
        localStorage.removeItem(`input_log_${questionnaireId}`);
      } catch (clearError) {
        console.warn('Failed to clear corrupted input log data:', clearError);
      }
    }
    return {};
  });

  const [fileErrors, setFileErrors] = useState({});
  const pendingFilesRef = useRef({});

  const openUnansweredModal = useCallback((questions = []) => {
    setUnansweredModalState({
      isOpen: true,
      questions: Array.isArray(questions) ? questions : []
    });
  }, []);

  const closeUnansweredModal = useCallback(() => {
    setUnansweredModalState((prev) => ({
      ...prev,
      isOpen: false
    }));
  }, []);

  useEffect(() => {
    if (!unansweredModalState.isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeUnansweredModal();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [unansweredModalState.isOpen, closeUnansweredModal]);

  const normalizedQuestionnaire = useMemo(
    () => normalizeQuestionnaireData(questionnaire || {}),
    [questionnaire]
  );

  const customQuestionPages = useMemo(() => {
    const pages = normalizedQuestionnaire.questionPages || [];
    return pages.filter((page) => Array.isArray(page?.questions) && page.questions.length > 0);
  }, [normalizedQuestionnaire]);

  const customQuestions = useMemo(
    () => customQuestionPages.flatMap((page) => page.questions || []),
    [customQuestionPages]
  );

  const sectionsText = questionnaire?.intro_page?.sectionsText || questionnaire?.introSectionsText || DEFAULT_SECTIONS_TEXT;
  const sectionLines = useMemo(
    () => String(sectionsText || DEFAULT_SECTIONS_TEXT).split('\n').map((line) => line.trim()).filter(Boolean),
    [sectionsText]
  );
  const defaultSectionLine = sectionLines[0] || '';
  const reviewSectionLine = sectionLines[sectionLines.length - 1] || '';
  const legacySpecificSectionLine = sectionLines.length <= 3 ? (sectionLines[1] || '') : '';
  const legacyDefaultSectionLabel = useMemo(
    () => getSectionLabelFromLine(defaultSectionLine, ''),
    [defaultSectionLine]
  );
  const legacyReviewSectionLabel = useMemo(
    () => getSectionLabelFromLine(reviewSectionLine, ''),
    [reviewSectionLine]
  );
  const defaultSectionLabel = useMemo(
    () => String((questionnaire?.intro_page?.defaultSectionTitle ?? questionnaire?.defaultSectionTitle ?? legacyDefaultSectionLabel) || '').trim(),
    [questionnaire?.intro_page?.defaultSectionTitle, questionnaire?.defaultSectionTitle, legacyDefaultSectionLabel]
  );
  const customSectionLabel = useMemo(
    () => getSectionLabelFromLine(legacySpecificSectionLine, ''),
    [legacySpecificSectionLine]
  );
  const reviewSectionLabel = useMemo(
    () => String((questionnaire?.intro_page?.reviewSectionTitle ?? questionnaire?.reviewSectionTitle ?? legacyReviewSectionLabel) || '').trim(),
    [questionnaire?.intro_page?.reviewSectionTitle, questionnaire?.reviewSectionTitle, legacyReviewSectionLabel]
  );
  const legacyDefaultSectionDescription = useMemo(
    () => getSectionDescriptionFromLine(defaultSectionLine, ''),
    [defaultSectionLine]
  );
  const legacyReviewSectionDescription = useMemo(
    () => getSectionDescriptionFromLine(reviewSectionLine, ''),
    [reviewSectionLine]
  );
  const defaultSectionDescription = useMemo(
    () => String((questionnaire?.intro_page?.defaultSectionDescription ?? questionnaire?.defaultSectionDescription ?? legacyDefaultSectionDescription) || '').trim(),
    [questionnaire?.intro_page?.defaultSectionDescription, questionnaire?.defaultSectionDescription, legacyDefaultSectionDescription]
  );
  const reviewSectionDescription = useMemo(
    () => String((questionnaire?.intro_page?.reviewSectionDescription ?? questionnaire?.reviewSectionDescription ?? legacyReviewSectionDescription) || '').trim(),
    [questionnaire?.intro_page?.reviewSectionDescription, questionnaire?.reviewSectionDescription, legacyReviewSectionDescription]
  );
  const introOverviewText = questionnaire?.intro_page?.overviewText || questionnaire?.introOverviewText || '';
  const introAfterSectionsText = questionnaire?.intro_page?.afterSectionsText || questionnaire?.introAfterSectionsText || '';
  const allowMultipleSubmissions = useMemo(
    () => Boolean(
      questionnaire?.allowMultipleSubmissions
      ?? questionnaire?.intro_page?.allowMultipleSubmissions
      ?? false
    ),
    [questionnaire?.allowMultipleSubmissions, questionnaire?.intro_page?.allowMultipleSubmissions]
  );
  const accessToken = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('t') || '';
    } catch {
      return '';
    }
  }, []);
  const defaultQuestionsPageOrder = useMemo(
    () => clampDefaultQuestionsPageOrder(
      questionnaire?.defaultQuestionsPageOrder ?? questionnaire?.intro_page?.defaultQuestionsPageOrder,
      customQuestionPages.length
    ),
    [questionnaire?.defaultQuestionsPageOrder, questionnaire?.intro_page?.defaultQuestionsPageOrder, customQuestionPages.length]
  );
  const hasMultipleCustomPages = customQuestionPages.length > 1;
  const questionFlowItems = useMemo(() => {
    const totalQuestionPages = customQuestionPages.length + 1;
    const items = [];
    let customCursor = 0;

    for (let position = 1; position <= totalQuestionPages; position += 1) {
      if (position === defaultQuestionsPageOrder) {
        items.push({
          id: 'default',
          type: 'default',
          label: defaultSectionLabel,
          description: defaultSectionDescription,
          order: position
        });
        continue;
      }

      const page = customQuestionPages[customCursor];
      if (page) {
        items.push({
          id: `custom_${page.id || customCursor}`,
          type: 'custom',
          customIndex: customCursor,
          page,
          label: String(page?.title || '').trim() || (hasMultipleCustomPages ? `${customSectionLabel} ${customCursor + 1}`.trim() : customSectionLabel),
          description: String(page?.description || '').trim(),
          order: position
        });
      }
      customCursor += 1;
    }

    return items;
  }, [customQuestionPages, defaultQuestionsPageOrder, defaultSectionLabel, defaultSectionDescription, hasMultipleCustomPages, customSectionLabel]);
  const introSectionItems = useMemo(() => {
    const defaultItem = {
      title: defaultSectionLabel,
      description: defaultSectionDescription
    };
    const customItems = customQuestionPages.map((page, index) => ({
      title: String(page?.title || '').trim() || `${customSectionLabel} ${index + 1}`.trim(),
      description: String(page?.description || '').trim()
    }));
    const orderedQuestionItems = [];
    let customCursor = 0;

    for (let position = 1; position <= customItems.length + 1; position += 1) {
      if (position === defaultQuestionsPageOrder) {
        orderedQuestionItems.push(defaultItem);
        continue;
      }

      if (customItems[customCursor]) {
        orderedQuestionItems.push(customItems[customCursor]);
      }
      customCursor += 1;
    }

    const reviewItem = {
      title: reviewSectionLabel,
      description: reviewSectionDescription
    };

    return [...orderedQuestionItems, reviewItem].filter((section) => String(section?.title || '').trim());
  }, [
    defaultSectionLabel,
    defaultSectionDescription,
    customQuestionPages,
    customSectionLabel,
    defaultQuestionsPageOrder,
    reviewSectionLabel,
    reviewSectionDescription
  ]);
  const currentCustomPage = customQuestionPages[customPageIndex] || null;
  const currentQuestionFlowIndex = useMemo(() => {
    if (currentStep === 'default') {
      return questionFlowItems.findIndex((item) => item.type === 'default');
    }
    if (currentStep === 'custom') {
      return questionFlowItems.findIndex((item) => item.type === 'custom' && item.customIndex === customPageIndex);
    }
    return -1;
  }, [currentStep, questionFlowItems, customPageIndex]);
  const isLastQuestionFlowPage = currentQuestionFlowIndex >= 0 && currentQuestionFlowIndex === questionFlowItems.length - 1;

  const stepperItems = useMemo(() => {
    return [
      { id: 'intro', label: 'Einführung' },
      ...questionFlowItems.map((item) => ({
        id: item.id,
        label: item.label
      })),
      { id: 'confirm', label: reviewSectionLabel }
    ];
  }, [questionFlowItems, reviewSectionLabel]);

  const activeStepIndex = useMemo(() => {
    if (currentStep === 'intro') return 0;
    if (currentStep === 'default' || currentStep === 'custom') {
      return currentQuestionFlowIndex >= 0 ? currentQuestionFlowIndex + 1 : 1;
    }
    return stepperItems.length - 1;
  }, [currentStep, currentQuestionFlowIndex, stepperItems.length]);

  const currentStepLabel = useMemo(() => {
    if (currentStep === 'intro') return 'Einführung';
    if (currentStep === 'default' || currentStep === 'custom') {
      return questionFlowItems[currentQuestionFlowIndex]?.label || (currentStep === 'default' ? defaultSectionLabel : customSectionLabel);
    }
    return reviewSectionLabel;
  }, [currentStep, questionFlowItems, currentQuestionFlowIndex, defaultSectionLabel, customSectionLabel, reviewSectionLabel]);
  const currentStepDescription = useMemo(() => {
    if (currentStep === 'intro') return '';
    if (currentStep === 'default' || currentStep === 'custom') {
      return String(questionFlowItems[currentQuestionFlowIndex]?.description || '').trim();
    }
    return String(reviewSectionDescription || '').trim();
  }, [currentStep, questionFlowItems, currentQuestionFlowIndex, reviewSectionDescription]);

  useEffect(() => {
    setCustomPageIndex((prev) => {
      if (customQuestionPages.length === 0) return 0;
      return Math.min(prev, customQuestionPages.length - 1);
    });
  }, [customQuestionPages.length]);

  useEffect(() => {
    if (hasAppliedInitialQPageRef.current) return;
    if (currentStep !== 'default' && currentStep !== 'custom') return;

    hasAppliedInitialQPageRef.current = true;
    try {
      const queryPage = parseInt(new URLSearchParams(window.location.search).get('qpage') || '', 10);
      if (!Number.isFinite(queryPage) || queryPage < 1) return;

      const targetFlowItem = questionFlowItems[queryPage - 1];
      if (!targetFlowItem) return;

      if (targetFlowItem.type === 'default') {
        setCurrentStep('default');
      } else {
        setCustomPageIndex(targetFlowItem.customIndex || 0);
        setCurrentStep('custom');
      }
    } catch (error) {
      // ignore malformed query values
    }
  }, [currentStep, questionFlowItems]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if ((currentStep === 'default' || currentStep === 'custom') && currentQuestionFlowIndex >= 0) {
      url.searchParams.set('qpage', String(currentQuestionFlowIndex + 1));
    } else {
      url.searchParams.delete('qpage');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }, [currentStep, currentQuestionFlowIndex]);

  // Helper function to update consolidated input log
  const updateInputLog = useCallback((questionId, questionText, questionType, value, action = 'UPDATED') => {
    const logEntry = {
      questionText: questionText,
      questionType: questionType,
      currentValue: value,
      action: action,
      timestamp: new Date().toISOString(),
      step: currentStep
    };

    // Update the log object (overwrites old entries for same questionId)
    setInputLog(prevLog => {
      const newInputLog = {
        ...prevLog,
        [questionId]: logEntry
      };
      
      // Save to localStorage
      try {
        localStorage.setItem(`input_log_${questionnaireId}`, JSON.stringify(newInputLog));
      } catch (error) {
        console.warn('Could not save input log to localStorage:', error);
      }
      
      return newInputLog;
    });

    // Note: Input log is now submitted to database instead of console logging
    
    return logEntry;
  }, [currentStep, questionnaireId]);

  // Independent theme management for applicant form only
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  // Clear form data from localStorage
  const clearFormData = useCallback(() => {
    try {
      localStorage.removeItem(`form_answers_${questionnaireId}`);
      localStorage.removeItem(`form_step_${questionnaireId}`);
      localStorage.removeItem(`form_datenschutz_${questionnaireId}`);
      localStorage.removeItem(`timer_started_${questionnaireId}`);
      localStorage.removeItem(`timer_start_time_${questionnaireId}`);
      localStorage.removeItem(`input_log_${questionnaireId}`);
      localStorage.removeItem(`auto_submit_triggered_${questionnaireId}`);
      // Reset submission flag
      isSubmittingRef.current = false;
    } catch (error) {
      console.warn('Could not clear form data from localStorage:', error);
    }
  }, [questionnaireId]);

  const markSubmittedInUrl = useCallback(() => {
    const url = new URL(window.location);
    url.searchParams.set('submitted', 'true');
    window.history.pushState({ submitted: true }, '', url.toString());
  }, []);

  const validateAccessToken = useCallback(async () => {
    if (allowMultipleSubmissions) {
      return { valid: true, token: null };
    }

    if (!accessToken) {
      return { valid: false, token: null };
    }

    const tokenRecord = await dbService.getQuestionnaireAccessToken(accessToken);
    if (!tokenRecord) {
      return { valid: false, token: null };
    }

    const belongsToQuestionnaire = String(tokenRecord.questionnaireId || '') === String(questionnaireId);
    if (!belongsToQuestionnaire) {
      return { valid: false, token: null };
    }

    if (tokenRecord.status !== 'active') {
      return { valid: false, token: null };
    }

    // Double-check: if a submission with this token already exists, it's been used
    const alreadyUsed = await dbService.isAccessTokenAlreadyUsed(accessToken);
    if (alreadyUsed) {
      return { valid: false, token: null };
    }

    return { valid: true, token: accessToken };
  }, [allowMultipleSubmissions, accessToken, questionnaireId]);

  // Token consumption is handled implicitly: validateAccessToken checks
  // if a submission with this token already exists via isAccessTokenAlreadyUsed().
  // No need to update the token record (public users can't update PocketBase records).

  // Check if a question is answered
  const isQuestionAnswered = useCallback((questionId, questionType, answerType = null, options = null) => {
    const answer = answers[questionId];
    if (!answer) return false;

    const effectiveQuestionType = questionType === 'image'
      ? (answerType || 'text')
      : questionType;

    if (effectiveQuestionType === 'file-upload') {
      return Array.isArray(answer?.fileNames) && answer.fileNames.length > 0;
    }

    if (effectiveQuestionType === 'matrix' || effectiveQuestionType === 'combobox') {
      return Array.isArray(answer) && answer.length > 0 && answer.every(r => r.label && r.value);
    }

    if (effectiveQuestionType === 'ranking') {
      const totalOptions = Array.isArray(options) ? options.length : 0;
      return Array.isArray(answer) && totalOptions > 0 && answer.length === totalOptions;
    }

    if (effectiveQuestionType === 'multiple-choice') {
      return Array.isArray(answer) && answer.length > 0;
    }

    return typeof answer === 'string' && answer.trim().length > 0;
  }, [answers]);

  // Auto-submit function when timer expires
  const handleAutoSubmit = useCallback(async () => {
    // Defense-in-depth: never auto-submit unless explicitly enabled on the
    // questionnaire. Callers must already gate on this, but a silent submission
    // is destructive (consumes the access token) so we re-check here.
    const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true ||
                                questionnaire?.intro_page?.autoSubmitEnabled === true;
    if (!isAutoSubmitEnabled) {
      return;
    }

    if (submitted) {
      return;
    }

    // Prevent multiple auto-submissions by checking if already in progress
    if (isSubmittingRef.current) {
      return;
    }
    
    // Check if auto-submit has already been triggered for this session
    try {
      const autoSubmitTriggered = localStorage.getItem(`auto_submit_triggered_${questionnaireId}`);
      if (autoSubmitTriggered === 'true') {
        return;
      }
      // Mark auto-submit as triggered
      localStorage.setItem(`auto_submit_triggered_${questionnaireId}`, 'true');
    } catch (error) {
      console.warn('Could not check/set auto-submit flag:', error);
    }
    
    isSubmittingRef.current = true;
    
    // Define isQuestionAnswered inline to avoid dependency issues
    const checkQuestionAnswered = (questionId, questionType, answerType = null, options = null) => {
      const answer = answers[questionId];
      if (!answer) return false;

      const effectiveQuestionType = questionType === 'image'
        ? (answerType || 'text')
        : questionType;

      if (effectiveQuestionType === 'matrix' || effectiveQuestionType === 'combobox') {
        return Array.isArray(answer) && answer.length > 0 && answer.every(r => r.label && r.value);
      }

      if (effectiveQuestionType === 'ranking') {
        const totalOptions = Array.isArray(options) ? options.length : 0;
        return Array.isArray(answer) && totalOptions > 0 && answer.length === totalOptions;
      }

      if (effectiveQuestionType === 'multiple-choice') {
        return Array.isArray(answer) && answer.length > 0;
      }

      return typeof answer === 'string' && answer.trim().length > 0;
    };
    
    try {
      const defaultQuestions = questionnaire?.defaultQuestions || [];
      const answeredDefaultQuestions = defaultQuestions.filter((q) => checkQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
      const answeredCustomQuestions = customQuestions.filter((q) => checkQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
      const totalQuestions = defaultQuestions.length + customQuestions.length;
      const tokenValidation = await validateAccessToken();
      if (!tokenValidation.valid) {
        setInvalidAccessToken(true);
        return;
      }
      const effectiveAccessToken = tokenValidation.token;

      // Create submission data with current answers
      const submissionData = {
        questionnaireId,
        questionnaire: questionnaireId,
        accessToken: effectiveAccessToken,
        applicantName: applicantName || 'Unbekannt',
        datenschutzConsent,
        answers: answers,
        submittedAt: new Date().toISOString(),
        submissionType: 'auto-submit',
        // Include all the same data structure as manual submission
        detailedAnswers: {
          defaultQuestions: defaultQuestions.map(q => ({
            id: q.id,
            text: q.text,
            type: q.type,
            options: q.options || [],
            answer: answers[q.id] || '',
            answered: checkQuestionAnswered(q.id, q.type, q.answerType, q.options)
          })),
          customQuestions: customQuestions.map(q => ({
            id: q.id,
            text: q.text,
            type: q.type,
            options: q.options || [],
            answer: answers[q.id] || (q.type === 'multiple-choice' || q.type === 'ranking' || q.type === 'matrix' || q.type === 'combobox' ? [] : ''),
            answered: checkQuestionAnswered(q.id, q.type, q.answerType, q.options)
          }))
        },
        completionSummary: {
          totalQuestions,
          answeredQuestions: answeredDefaultQuestions + answeredCustomQuestions,
          unansweredQuestions: totalQuestions - (answeredDefaultQuestions + answeredCustomQuestions),
          completionPercentage: totalQuestions > 0
            ? Math.round(((answeredDefaultQuestions + answeredCustomQuestions) / totalQuestions) * 100)
            : 0
        },
        completionDuration: (() => {
          try {
            const savedStartTime = localStorage.getItem(`timer_start_time_${questionnaireId}`);
            if (savedStartTime) {
              return Math.round((Date.now() - parseInt(savedStartTime)) / 1000);
            }
            return startTime ? Math.round((Date.now() - startTime) / 1000) : null;
          } catch (error) {
            console.warn('Could not calculate completion duration:', error);
            return null;
          }
        })(),
        // Include the consolidated input log for auto-submission
        answerLog: {
          applicantName: applicantName || 'Not provided',
          questionnaireId: questionnaireId,
          interactions: inputLog
        }
      };

      await dbService.createSubmission(submissionData);

      // Clear stored form data after successful submission
      clearFormData();
      
      setSubmitted(true);
      
      // Update URL to reflect submitted state
      markSubmittedInUrl();
    } catch (error) {
      const duplicateError = Boolean(
        error?.data?.data?.access_token
        || error?.data?.data?.questionnaire
        || String(error?.message || '').toLowerCase().includes('duplicate')
      );
      if (duplicateError) {
        setSubmitted(true);
        markSubmittedInUrl();
        return;
      }
      console.error("Error in auto-submission:", error);
      // Don't show alert for auto-submission errors to avoid interrupting user
    } finally {
      // Reset submission flag
      isSubmittingRef.current = false;
    }
  }, [submitted, answers, questionnaireId, applicantName, questionnaire, startTime, clearFormData, inputLog, datenschutzConsent, customQuestions, validateAccessToken, markSubmittedInUrl]);

  // Store the latest handleAutoSubmit function in a ref to avoid dependency issues
  handleAutoSubmitRef.current = handleAutoSubmit;


  // Check if already submitted on component mount
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      // First check URL params for immediate feedback
      const urlParams = new URLSearchParams(window.location.search);
      const isSubmitted = urlParams.get('submitted') === 'true';
      if (isSubmitted) {
        // Check if it's a withdrawal by checking localStorage
        try {
          const isWithdrawnFlag = localStorage.getItem(`quiz_withdrawn_${questionnaireId}`) === 'true';
          if (isWithdrawnFlag) {
            setIsWithdrawn(true);
            return;
          }
        } catch (error) {
          console.warn('Could not check withdrawal flag:', error);
        }
        setSubmitted(true);
        return;
      }
      
      if (!questionnaire) return;

      if (allowMultipleSubmissions) {
        return;
      }

      try {
        const tokenValidation = await validateAccessToken();
        if (!tokenValidation.valid) {
          setInvalidAccessToken(true);
        }
      } catch (error) {
        console.error('Error checking token status:', error);
      }
    };

    checkSubmissionStatus();
  }, [questionnaireId, questionnaire, allowMultipleSubmissions, validateAccessToken, markSubmittedInUrl]);

  // Set tab title for public questionnaire page
  useEffect(() => {
    // Check if we're on karrieresichern.de domain
    if (window.location.hostname === 'karrieresichern.de') {
      document.title = 'Karrieresichern';
    } else {
      const title = questionnaire?.title ? `Bewerbung – ${questionnaire.title}` : 'Bewerbung – Fragebogen';
      document.title = title;
    }
  }, [questionnaire?.title]);

  useEffect(() => {
    const load = async () => {
      setIsQuestionnaireLoading(true);
      try {
        const q = await dbService.getQuestionnaire(questionnaireId);
        if (q) {
          const normalized = normalizeQuestionnaireData(q);
          // Runtime migration: prepend NAME_QUESTION_SEED if missing
          const dq = normalized.defaultQuestions || [];
          if (!dq.some(question => question.id === 'system_name')) {
            normalized.defaultQuestions = [NAME_QUESTION_SEED, ...dq];
          }
          setQuestionnaire(normalized);
        } else {
          setQuestionnaire(null);
        }
      } catch (error) {
        console.error('Error loading questionnaire:', error);
        setQuestionnaire(null);
      } finally {
        setIsQuestionnaireLoading(false);
      }
    };
    load();
  }, [questionnaireId]);

  // Handle browser back/forward navigation for applicant form
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const isSubmitted = urlParams.get('submitted') === 'true';
      if (isSubmitted) {
        setSubmitted(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Listen for localStorage changes to keep state in sync
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === `form_answers_${questionnaireId}` && e.newValue) {
        try {
          const newAnswers = JSON.parse(e.newValue);
          setAnswers(prevAnswers => {
            // Only update if the answers actually changed to prevent infinite loops
            if (JSON.stringify(prevAnswers) !== JSON.stringify(newAnswers)) {
              return newAnswers;
            }
            return prevAnswers;
          });
        } catch (error) {
          console.warn('Could not parse localStorage answers:', error);
        }
      }
      if (e.key === `form_step_${questionnaireId}` && e.newValue) {
        setCurrentStep(prevStep => prevStep !== e.newValue ? e.newValue : prevStep);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [questionnaireId]);

  // Initialize timer state on component load
  useEffect(() => {
    const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true ||
                                questionnaire?.intro_page?.autoSubmitEnabled === true;

    // When auto-submit is off, the timer subsystem must not exist. Purge any
    // stale keys left over from a session where the flag was previously on —
    // otherwise a returning applicant would trip an "expired" timer and get
    // submitted unconditionally, consuming their access token.
    if (!isAutoSubmitEnabled) {
      try {
        localStorage.removeItem(`timer_started_${questionnaireId}`);
        localStorage.removeItem(`timer_start_time_${questionnaireId}`);
        localStorage.removeItem(`auto_submit_triggered_${questionnaireId}`);
      } catch (error) {
        console.warn('Could not clear stale timer state:', error);
      }
      return;
    }

    try {
      const savedTimerStarted = localStorage.getItem(`timer_started_${questionnaireId}`);
      const savedStartTime = localStorage.getItem(`timer_start_time_${questionnaireId}`);

      if (savedTimerStarted === 'true' && savedStartTime) {
        const startTime = parseInt(savedStartTime);
        const endTime = startTime + (15 * 60 * 1000); // 15 minutes from start
        const remainingMs = endTime - Date.now();
        const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

        // Set timer state
        setTimeLeft(remainingSeconds);
        setTimerStarted(true);
        setStartTime(startTime);

        // If timer has already expired, auto submit immediately
        if (remainingSeconds <= 0) {
          // Use setTimeout to ensure handleAutoSubmit is available
          setTimeout(() => {
            // Only auto-submit if not already submitted or submitting
            if (!submitted && !isSubmittingRef.current && handleAutoSubmitRef.current) {
              handleAutoSubmitRef.current();
            }
          }, 100);
        }
      }
    } catch (error) {
      console.warn('Could not initialize timer state:', error);
      // Clear corrupted timer data
      try {
        localStorage.removeItem(`timer_started_${questionnaireId}`);
        localStorage.removeItem(`timer_start_time_${questionnaireId}`);
        localStorage.removeItem(`auto_submit_triggered_${questionnaireId}`);
      } catch (clearError) {
        console.warn('Could not clear corrupted timer data:', clearError);
      }
    }
  }, [questionnaireId, submitted, questionnaire?.autoSubmitEnabled, questionnaire?.intro_page?.autoSubmitEnabled]);

  // Timer effect - handles timer persistence and time tracking
  useEffect(() => {
    const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true ||
                                questionnaire?.intro_page?.autoSubmitEnabled === true;

    // No auto-submit → no ticking timer at all. Clear any leftover interval and bail.
    if (!isAutoSubmitEnabled) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    if (timerStarted && currentStep !== 'intro' && !submitted) {
      // Clear any existing timer first
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      const interval = setInterval(() => {
        // Calculate remaining time based on stored start time
        try {
          const savedStartTime = localStorage.getItem(`timer_start_time_${questionnaireId}`);
          if (savedStartTime) {
            const startTime = parseInt(savedStartTime);
            const endTime = startTime + (15 * 60 * 1000); // 15 minutes from start
            const remainingMs = endTime - Date.now();
            const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
            
            setTimeLeft(remainingSeconds);
            
            if (remainingSeconds <= 0) {
              // Timer expired - check if auto-submit is enabled
              clearInterval(interval);
              timerIntervalRef.current = null;
              setTimeLeft(0);
              
              // Check if auto-submit is enabled
              const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true || 
                                        questionnaire?.intro_page?.autoSubmitEnabled === true;
              
              if (isAutoSubmitEnabled) {
                // Auto-submit is enabled - submit the form
                console.log('Timer expired - auto-submitting form');
                
                // Clear timer data to prevent multiple submissions
                try {
                  localStorage.removeItem(`timer_started_${questionnaireId}`);
                  localStorage.removeItem(`timer_start_time_${questionnaireId}`);
                  localStorage.removeItem(`auto_submit_triggered_${questionnaireId}`);
                } catch (error) {
                  console.warn('Could not clear timer data on expiration:', error);
                }
                
                // Only auto-submit if not already submitted or submitting
                if (!submitted && !isSubmittingRef.current && handleAutoSubmitRef.current) {
                  handleAutoSubmitRef.current();
                }
              } else {
                // Auto-submit is disabled - just log completion
                console.log('Timer expired - time tracking complete (no auto-submit)');
              }
            }
          } else {
            // No start time found, clear timer
            clearInterval(interval);
            timerIntervalRef.current = null;
          }
        } catch (error) {
          console.warn('Could not calculate remaining time:', error);
          // Clear corrupted timer data
          try {
            localStorage.removeItem(`timer_started_${questionnaireId}`);
            localStorage.removeItem(`timer_start_time_${questionnaireId}`);
            localStorage.removeItem(`auto_submit_triggered_${questionnaireId}`);
          } catch (clearError) {
            console.warn('Could not clear corrupted timer data:', clearError);
          }
          // Stop the timer
          clearInterval(interval);
          timerIntervalRef.current = null;
        }
      }, 1000);
      
      timerIntervalRef.current = interval;
    } else {
      // Clear timer if conditions are not met
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timerStarted, currentStep, questionnaireId, submitted, questionnaire?.autoSubmitEnabled, questionnaire?.intro_page?.autoSubmitEnabled]);

  // Handle browser close/refresh events
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Check if auto-submit is enabled
      const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true || 
                                questionnaire?.intro_page?.autoSubmitEnabled === true;
      
      // Only show warning if auto-submit is enabled, form has been started and not submitted
      if (isAutoSubmitEnabled && timerStarted && !submitted && currentStep !== 'intro') {
        // Show warning to user
        const message = 'Ihre Antworten werden nicht gespeichert, wenn Sie die Seite verlassen. Möchten Sie wirklich gehen?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [timerStarted, submitted, currentStep, questionnaireId, questionnaire?.autoSubmitEnabled, questionnaire?.intro_page?.autoSubmitEnabled]);

  // Save form data to localStorage and sync state
  const saveFormData = (newAnswers, _unused, newStep, newDatenschutzConsent) => {
    try {
      if (newAnswers !== undefined) {
        localStorage.setItem(`form_answers_${questionnaireId}`, JSON.stringify(newAnswers));
        setAnswers(prevAnswers => {
          if (JSON.stringify(prevAnswers) !== JSON.stringify(newAnswers)) {
            return newAnswers;
          }
          return prevAnswers;
        });
      }
      if (newStep !== undefined) {
        localStorage.setItem(`form_step_${questionnaireId}`, newStep);
        setCurrentStep(prevStep => prevStep !== newStep ? newStep : prevStep);
      }
      if (newDatenschutzConsent !== undefined) {
        localStorage.setItem(`form_datenschutz_${questionnaireId}`, newDatenschutzConsent.toString());
        setDatenschutzConsent(prevConsent => prevConsent !== newDatenschutzConsent ? newDatenschutzConsent : prevConsent);
      }
    } catch (error) {
      console.warn('Could not save form data to localStorage:', error);
      if (newAnswers !== undefined) {
        setAnswers(prevAnswers => {
          if (JSON.stringify(prevAnswers) !== JSON.stringify(newAnswers)) {
            return newAnswers;
          }
          return prevAnswers;
        });
      }
      if (newStep !== undefined) {
        setCurrentStep(prevStep => prevStep !== newStep ? newStep : prevStep);
      }
      if (newDatenschutzConsent !== undefined) {
        setDatenschutzConsent(prevConsent => prevConsent !== newDatenschutzConsent ? newDatenschutzConsent : prevConsent);
      }
    }
  };


  // Timer functions
  const startTimer = () => {
    
    // Prevent starting timer if already submitted
    if (submitted) {
      return;
    }
    
    // Validate datenschutz consent before starting
    if (!datenschutzConsent) {
      setShowDatenschutzError(true);
      return;
    }
    
    const isAutoSubmitEnabled = questionnaire?.autoSubmitEnabled === true ||
                                questionnaire?.intro_page?.autoSubmitEnabled === true;

    const startTimeValue = Date.now();

    setTimerStarted(true);
    setStartTime(startTimeValue);
    setTimeLeft(15 * 60);

    // Only persist timer keys when auto-submit is actually enabled. Persisting
    // them otherwise would arm an expiry that a returning applicant trips on
    // page load — see the mount-time guard above.
    if (isAutoSubmitEnabled) {
      try {
        localStorage.setItem(`timer_started_${questionnaireId}`, 'true');
        localStorage.setItem(`timer_start_time_${questionnaireId}`, startTimeValue.toString());
      } catch (error) {
        console.warn('Could not save timer state:', error);
      }
    }

    const firstQuestionFlowItem = questionFlowItems[0];
    if (firstQuestionFlowItem?.type === 'custom') {
      setCustomPageIndex(firstQuestionFlowItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    } else {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    }
  };
  const shouldShowConditionalQuestion = useCallback((question, currentAnswers = answers) => {
    return isConditionalSatisfied(question?.conditional, currentAnswers);
  }, [answers]);

  // Accepts either a final value OR an updater function (prevValueForQId) => newValue.
  // Always merges against the latest state via functional setState so concurrent
  // updates (mobile autofill racing user typing, IME composition, multi-row
  // matrix edits, batched events) can't clobber each other through a stale
  // closure-captured snapshot.
  const handleAnswerChange = (qId, valueOrUpdater) => {
    const findQuestionText = (id) => {
      const defaultQuestion = (questionnaire?.defaultQuestions || []).find(q => q.id === id);
      if (defaultQuestion) return defaultQuestion.text;
      const customQuestion = customQuestions.find(q => q.id === id);
      if (customQuestion) return customQuestion.text;
      return `Question ID: ${id}`;
    };

    setAnswers(prevAnswers => {
      const resolvedValue = typeof valueOrUpdater === 'function'
        ? valueOrUpdater(prevAnswers[qId])
        : valueOrUpdater;

      updateInputLog(qId, findQuestionText(qId), 'text/single-choice', resolvedValue, 'UPDATED');

      const newAnswers = { ...prevAnswers, [qId]: resolvedValue };

      const allConditionalCandidates = [
        ...(questionnaire?.defaultQuestions || []),
        ...customQuestions
      ];
      allConditionalCandidates.forEach(question => {
        if (question.conditional && question.conditional.dependsOn === qId) {
          if (!isConditionalSatisfied(question.conditional, newAnswers)) {
            newAnswers[question.id] = '';
            updateInputLog(question.id, findQuestionText(question.id), 'conditional', '', 'CLEARED');
          }
        }
      });

      try {
        localStorage.setItem(`form_answers_${questionnaireId}`, JSON.stringify(newAnswers));
      } catch (error) {
        console.warn('Could not save form data to localStorage:', error);
      }

      return newAnswers;
    });
  };

  // --- File upload handlers (deferred — files stay in memory until submit) ---
  const handleFileSelect = (questionId, file, question) => {
    const accepted = question.acceptedFileTypes || [];
    if (accepted.length > 0 && !accepted.includes(file.type)) {
      setFileErrors(prev => ({ ...prev, [questionId]: 'Dateityp nicht erlaubt.' }));
      return;
    }
    const maxMB = question.maxFileSize || 10;
    if (file.size > maxMB * 1024 * 1024) {
      setFileErrors(prev => ({ ...prev, [questionId]: `Datei zu groß (max. ${maxMB} MB).` }));
      return;
    }
    setFileErrors(prev => ({ ...prev, [questionId]: null }));

    // Store File object in ref (not serializable, so not in answers/localStorage)
    const pending = pendingFilesRef.current[questionId] || [];
    pendingFilesRef.current[questionId] = [...pending, file];

    handleAnswerChange(questionId, (prev) => {
      const base = prev || { fileNames: [], note: '' };
      return {
        fileNames: [...(base.fileNames || []), file.name],
        note: base.note || ''
      };
    });
  };

  const handleFileRemove = (questionId, fileIndex) => {
    const pending = pendingFilesRef.current[questionId] || [];
    pendingFilesRef.current[questionId] = pending.filter((_, i) => i !== fileIndex);

    handleAnswerChange(questionId, (prev) => {
      const base = prev || { fileNames: [], note: '' };
      return {
        fileNames: (base.fileNames || []).filter((_, i) => i !== fileIndex),
        note: base.note || ''
      };
    });
  };

  const handleFileNoteChange = (questionId, note) => {
    handleAnswerChange(questionId, (prev) => {
      const base = prev || { fileNames: [], note: '' };
      return { ...base, note };
    });
  };

  const handleMultiChoiceChange = (qId, option) => { 
    const currentAnswers = answers[qId] || []; 
    const isAdding = !currentAnswers.includes(option);
    
    // Find the question to check maxSelections limit
    const findQuestion = (id) => {
      // Check default questions first
      const defaultQuestion = (questionnaire?.defaultQuestions || []).find(q => q.id === id);
      if (defaultQuestion) return defaultQuestion;
      
      // Check custom questions
      const customQuestion = customQuestions.find(q => q.id === id);
      if (customQuestion) return customQuestion;
      
      return null;
    };
    
    const question = findQuestion(qId);
    
    // Check maxSelections limit if adding a new option
    if (isAdding && question?.maxSelections && currentAnswers.length >= question.maxSelections) {
      // Show a brief notification that limit is reached
      console.log(`Maximum ${question.maxSelections} selections allowed for this question`);
      return; // Don't add the option
    }
    
    const newAnswers = isAdding ? 
      [...currentAnswers, option] : 
      currentAnswers.filter(a => a !== option);
    
    // Find the question text for better readability
    const findQuestionText = (id) => {
      const q = findQuestion(id);
      return q ? q.text : `Question ID: ${id}`;
    };
    
    // Update consolidated input log
    updateInputLog(qId, findQuestionText(qId), 'multiple-choice', newAnswers, isAdding ? 'OPTION_ADDED' : 'OPTION_REMOVED');
    
    // Multiple choice tracking is handled by updateInputLog() and submitted to database
    
    handleAnswerChange(qId, newAnswers); 
  };
  
  const handleRankingChange = (qId, option) => {
    const currentAnswers = answers[qId] || [];
    const isAdding = !currentAnswers.includes(option);
    const newAnswers = isAdding ? 
      [...currentAnswers, option] :               // Add to end if not selected
      currentAnswers.filter(a => a !== option);  // Remove if already selected
    
    // Find the question text for better readability
    const findQuestionText = (id) => {
      // Check default questions first
      const defaultQuestion = (questionnaire?.defaultQuestions || []).find(q => q.id === id);
      if (defaultQuestion) return defaultQuestion.text;
      
      // Check custom questions
      const customQuestion = customQuestions.find(q => q.id === id);
      if (customQuestion) return customQuestion.text;
      
      return `Question ID: ${id}`;
    };
    
    // Update consolidated input log
    updateInputLog(qId, findQuestionText(qId), 'ranking', newAnswers, isAdding ? 'RANK_ADDED' : 'RANK_REMOVED');
    
    // Ranking tracking is handled by updateInputLog() and submitted to database
    
    handleAnswerChange(qId, newAnswers);
  };
  
  // Step navigation functions
  const validateCustomQuestionList = useCallback((questionsToValidate = [], pageTitle = '') => {
    const unansweredQuestions = [];
    const pagePrefix = pageTitle ? `${pageTitle} - ` : '';
    const labelMap = computeDisplayLabels(questionsToValidate);
    const visibleQuestions = questionsToValidate.filter((question) => shouldShowConditionalQuestion(question, answers));

    visibleQuestions.forEach((question, index) => {
      if (!question) return;

      const answer = answers[question.id];
      const questionNumber = labelMap[question.id] || (index + 1);

      // Matrix/combobox: orphan rows (value without label or vice versa) are invalid
      // regardless of whether the question is required, since they produce useless data.
      if (question.type === 'matrix' || question.type === 'combobox') {
        const rows = Array.isArray(answer) ? answer : [];
        const isSingleMode = (question.matrixSelectionMode || 'per-row') === 'single';
        // In single mode, only one row has a value; other rows with labels are NOT orphans.
        // Orphan = a row with a value but no label (or, in per-row mode, label without value).
        const hasOrphan = isSingleMode
          ? rows.some(r => r && r.value && !r.label)
          : rows.some(r => r && ((r.label && !r.value) || (!r.label && r.value)));
        const hasComplete = rows.some(r => r && r.label && r.value);
        if (question.required === false) {
          if (hasOrphan) unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        } else {
          if (!hasComplete || hasOrphan) unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
        return;
      }

      if (question.required === false) return;

      if (question.type === 'text') {
        if (!answer || answer.trim() === '') {
          unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
      } else if (question.type === 'single-choice') {
        if (!answer || typeof answer !== 'string' || answer.trim() === '') {
          unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
      } else if (question.type === 'multiple-choice') {
        if (!answer || !Array.isArray(answer) || answer.length === 0) {
          unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
      } else if (question.type === 'ranking') {
        const totalOptions = Array.isArray(question.options) ? question.options.length : 0;
        if (!answer || !Array.isArray(answer) || answer.length < totalOptions) {
          unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
      } else if (question.type === 'image') {
        if (!question.answerType || question.answerType === 'text') {
          if (!answer || answer.trim() === '') {
            unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
          }
        } else if (question.answerType === 'single-choice') {
          if (!answer || typeof answer !== 'string' || answer.trim() === '') {
            unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
          }
        } else if (question.answerType === 'multiple-choice') {
          if (!answer || !Array.isArray(answer) || answer.length === 0) {
            unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
          }
        }
      } else if (question.type === 'file-upload') {
        const fileNames = Array.isArray(answer?.fileNames) ? answer.fileNames : [];
        if (fileNames.length === 0) {
          unansweredQuestions.push(`${pagePrefix}Frage ${questionNumber}`);
        }
      }
    });

    return unansweredQuestions;
  }, [answers, shouldShowConditionalQuestion]);

  const validateDefaultQuestions = () => {
    const unansweredDefaultQuestions = [];
    const defaultQuestions = questionnaire?.defaultQuestions || [];
    const defaultLabelMap = computeDisplayLabels(defaultQuestions, 0);

    defaultQuestions.forEach((question, index) => {
      // Skip if question is undefined
      if (!question) return;

      const answer = answers[question.id];
      const questionNumber = getQuestionNumber(question, defaultLabelMap);

      if (!shouldShowConditionalQuestion(question, answers)) return;

      // Matrix/combobox: orphan rows are invalid regardless of required flag
      if (question.type === 'matrix' || question.type === 'combobox') {
        const rows = Array.isArray(answer) ? answer : [];
        const isSingleMode = (question.matrixSelectionMode || 'per-row') === 'single';
        const hasOrphan = isSingleMode
          ? rows.some(r => r && r.value && !r.label)
          : rows.some(r => r && ((r.label && !r.value) || (!r.label && r.value)));
        const hasComplete = rows.some(r => r && r.label && r.value);
        if (question.required === false) {
          if (hasOrphan) unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        } else {
          if (!hasComplete || hasOrphan) unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
        return;
      }

      if (question.required === false) return;

      if (question.type === 'text') {
        if (!answer || answer.trim() === '') {
          unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
      } else if (question.type === 'single-choice') {
        if (!answer || typeof answer !== 'string' || answer.trim() === '') {
          unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
      } else if (question.type === 'multiple-choice') {
        if (!answer || !Array.isArray(answer) || answer.length === 0) {
          unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
      } else if (question.type === 'ranking') {
        const totalOptions = Array.isArray(question.options) ? question.options.length : 0;
        if (!answer || !Array.isArray(answer) || answer.length < totalOptions) {
          unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
      } else if (question.type === 'image') {
        if (!question.answerType || question.answerType === 'text') {
          if (!answer || answer.trim() === '') {
            unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
          }
        } else if (question.answerType === 'single-choice') {
          if (!answer || typeof answer !== 'string' || answer.trim() === '') {
            unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
          }
        } else if (question.answerType === 'multiple-choice') {
          if (!answer || !Array.isArray(answer) || answer.length === 0) {
            unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
          }
        }
      } else if (question.type === 'file-upload') {
        const fileNames = Array.isArray(answer?.fileNames) ? answer.fileNames : [];
        if (fileNames.length === 0) {
          unansweredDefaultQuestions.push(`Frage ${questionNumber}`);
        }
      }
    });

    return unansweredDefaultQuestions;
  };

  const goToCustomQuestions = () => {
    const unansweredDefaultQuestions = validateDefaultQuestions();

    if (unansweredDefaultQuestions.length > 0) {
      openUnansweredModal(unansweredDefaultQuestions);
      return;
    }

    const nextQuestionItem = questionFlowItems[currentQuestionFlowIndex + 1];
    if (!nextQuestionItem) {
      setCurrentStep('confirm');
      setShowConfirmForm(true);
      saveFormData(undefined, undefined, 'confirm');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return;
    }

    if (nextQuestionItem.type === 'custom') {
      setCustomPageIndex(nextQuestionItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    } else {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    }
    
    // Enhanced scroll to top with delay to ensure state update
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goToConfirmation = () => {
    const unansweredCustomQuestions = customQuestionPages.flatMap((page) =>
      validateCustomQuestionList(page?.questions || [], page?.title || '')
    );
    
    if (unansweredCustomQuestions.length > 0) {
      openUnansweredModal(unansweredCustomQuestions);
      return;
    }
    
    setCurrentStep('confirm');
    setShowConfirmForm(true);
    
    // Enhanced scroll to top with delay to ensure state update
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goToNextCustomPage = () => {
    const unansweredCurrentPageQuestions = validateCustomQuestionList(
      currentCustomPage?.questions || [],
      currentCustomPage?.title || ''
    );

    if (unansweredCurrentPageQuestions.length > 0) {
      openUnansweredModal(unansweredCurrentPageQuestions);
      return;
    }

    const nextQuestionItem = questionFlowItems[currentQuestionFlowIndex + 1];
    if (!nextQuestionItem) {
      goToConfirmation();
      return;
    }

    if (nextQuestionItem.type === 'default') {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    } else {
      setCustomPageIndex(nextQuestionItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goBackWithinCustom = () => {
    const previousQuestionItem = questionFlowItems[currentQuestionFlowIndex - 1];
    if (!previousQuestionItem) {
      goBackToIntro();
      return;
    }

    if (previousQuestionItem.type === 'default') {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    } else {
      setCustomPageIndex(previousQuestionItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goBackFromDefault = () => {
    const previousQuestionItem = questionFlowItems[currentQuestionFlowIndex - 1];
    if (!previousQuestionItem) {
      goBackToIntro();
      return;
    }

    if (previousQuestionItem.type === 'custom') {
      setCustomPageIndex(previousQuestionItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    } else {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goBackToIntro = () => {
    // Don't allow going back to intro once the timer has started
    if (timerStarted) {
      return;
    }
    setCurrentStep('intro');
    saveFormData(undefined, undefined, 'intro');
    
    // Enhanced scroll to top with delay to ensure state update
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const goBackToCustom = () => {
    const lastQuestionItem = questionFlowItems[questionFlowItems.length - 1];
    if (!lastQuestionItem) {
      goBackToIntro();
      return;
    }

    if (lastQuestionItem.type === 'default') {
      setCurrentStep('default');
      saveFormData(undefined, undefined, 'default');
    } else {
      setCustomPageIndex(lastQuestionItem.customIndex || 0);
      setCurrentStep('custom');
      saveFormData(undefined, undefined, 'custom');
    }
    setShowConfirmForm(false);
    
    // Enhanced scroll to top with delay to ensure state update
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  // Withdrawal handlers
  const handleWithdrawalRequest = () => {
    setShowWithdrawalModal(true);
  };

  const handleConfirmWithdrawal = async () => {
    // Prevent multiple submissions
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    
    try {
      const defaultQuestions = questionnaire?.defaultQuestions || [];
      const answeredDefaultQuestions = defaultQuestions.filter((q) => isQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
      const answeredCustomQuestions = customQuestions.filter((q) => isQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
      const totalQuestions = defaultQuestions.length + customQuestions.length;
      const tokenValidation = await validateAccessToken();
      if (!tokenValidation.valid) {
        setInvalidAccessToken(true);
        return;
      }
      const effectiveAccessToken = tokenValidation.token;

      // Create withdrawal submission data
      const withdrawalData = {
        questionnaireId,
        questionnaire: questionnaireId,
        accessToken: effectiveAccessToken,
        applicantName: applicantName || 'Unbekannt',
        datenschutzConsent,
        answers: answers,
        submittedAt: new Date().toISOString(),
        submissionType: 'withdrawal',
        status: 'Bewerbung zurückgezogen',
        // Include all the same data structure as normal submission
        detailedAnswers: {
          defaultQuestions: defaultQuestions.map(q => ({
            id: q.id,
            text: q.text,
            type: q.type,
            options: q.options || [],
            answer: answers[q.id] || '',
            answered: isQuestionAnswered(q.id, q.type, q.answerType, q.options)
          })),
          customQuestions: customQuestions.map(q => ({
            id: q.id,
            text: q.text,
            type: q.type,
            options: q.options || [],
            answer: answers[q.id] || (q.type === 'multiple-choice' || q.type === 'ranking' || q.type === 'matrix' || q.type === 'combobox' ? [] : ''),
            answered: isQuestionAnswered(q.id, q.type, q.answerType, q.options)
          }))
        },
        completionSummary: {
          totalQuestions,
          answeredQuestions: answeredDefaultQuestions + answeredCustomQuestions,
          unansweredQuestions: totalQuestions - (answeredDefaultQuestions + answeredCustomQuestions),
          completionPercentage: totalQuestions > 0
            ? Math.round(((answeredDefaultQuestions + answeredCustomQuestions) / totalQuestions) * 100)
            : 0
        },
        completionDuration: (() => {
          try {
            const savedStartTime = localStorage.getItem(`timer_start_time_${questionnaireId}`);
            if (savedStartTime) {
              return Math.round((Date.now() - parseInt(savedStartTime)) / 1000);
            }
            return startTime ? Math.round((Date.now() - startTime) / 1000) : null;
          } catch (error) {
            console.warn('Could not calculate completion duration:', error);
            return null;
          }
        })(),
        // Include the consolidated input log for withdrawal
        answerLog: {
          applicantName: applicantName || 'Not provided',
          questionnaireId: questionnaireId,
          interactions: inputLog
        }
      };
      
      // Create withdrawal submission
      await dbService.createSubmission(withdrawalData);

      // Clear stored form data after successful submission
      clearFormData();
      
      // Store withdrawal flag in localStorage to prevent duplicate submissions
      try {
        localStorage.setItem(`quiz_withdrawn_${questionnaireId}`, 'true');
      } catch (error) {
        console.warn('Could not save withdrawal flag to localStorage:', error);
      }
      
      setIsWithdrawn(true);
      setShowWithdrawalModal(false);
      
      // Update URL to reflect submitted state
      markSubmittedInUrl();
      
    } catch (error) {
      console.error("Error saving withdrawal submission:", error);
      alert('Fehler beim Speichern des Rückzugs. Bitte versuchen Sie es erneut.');
    } finally {
      // Reset submission flag
      isSubmittingRef.current = false;
    }
  };

  const handleStayInPool = () => {
    setShowWithdrawalModal(false);
  };

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    
    // Prevent multiple submissions
    if (submitted || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    
    // Console log for submission attempt
    console.log('🚀 SUBMISSION LOG - Form Submission Started:', {
      questionnaireId: questionnaireId,
      applicantName: applicantName,
      totalAnswers: Object.keys(answers).length,
      answers: answers,
      currentStep: currentStep,
      datenschutzConsent: datenschutzConsent,
      timestamp: new Date().toISOString()
    });
    
    const defaultQuestions = questionnaire?.defaultQuestions || [];
    const answeredDefaultQuestions = defaultQuestions.filter((q) => isQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
    const answeredCustomQuestions = customQuestions.filter((q) => isQuestionAnswered(q.id, q.type, q.answerType, q.options)).length;
    const totalQuestions = defaultQuestions.length + customQuestions.length;

    // Define submissionData outside try block so it's accessible in catch
    const submissionData = {
      questionnaireId,
      questionnaire: questionnaireId,
      accessToken: null,
      applicantName: applicantName || 'Unbekannt',
      datenschutzConsent,
      answers: answers,
      submissionType: 'normal',
      // Include detailed question structure for reference (not saved to PocketBase, but kept for consistency)
      detailedAnswers: {
        defaultQuestions: defaultQuestions.map(q => ({
          id: q.id,
          text: q.text,
          type: q.type,
          options: q.options || [],
          answer: answers[q.id] || '',
          answered: isQuestionAnswered(q.id, q.type, q.answerType, q.options)
        })),
        customQuestions: customQuestions.map(q => ({
          id: q.id,
          text: q.text,
          type: q.type,
          options: q.options || [],
          answer: answers[q.id] || (q.type === 'multiple-choice' || q.type === 'ranking' || q.type === 'matrix' || q.type === 'combobox' ? [] : ''),
          answered: isQuestionAnswered(q.id, q.type, q.answerType, q.options)
        }))
      },
      // Summary for HR - shows completion status
      completionSummary: {
        totalQuestions,
        answeredQuestions: answeredDefaultQuestions + answeredCustomQuestions,
        unansweredQuestions: totalQuestions - (answeredDefaultQuestions + answeredCustomQuestions),
        completionPercentage: totalQuestions > 0
          ? Math.round(((answeredDefaultQuestions + answeredCustomQuestions) / totalQuestions) * 100)
          : 0
      },
      // Track completion duration
      completionDuration: (() => {
        try {
          const savedStartTime = localStorage.getItem(`timer_start_time_${questionnaireId}`);
          if (savedStartTime) {
            return Math.round((Date.now() - parseInt(savedStartTime)) / 1000); // Duration in seconds
          }
          return startTime ? Math.round((Date.now() - startTime) / 1000) : null; // Fallback to state
        } catch (error) {
          console.warn('Could not calculate completion duration:', error);
          return null;
        }
      })(),
      // Include the consolidated input log
      answerLog: {
        applicantName: applicantName || 'Not provided',
        questionnaireId: questionnaireId,
        interactions: inputLog
      }
    };
    
    try {
      // Try to save to PocketBase
      const tokenValidation = await validateAccessToken();
      if (!tokenValidation.valid) {
        setInvalidAccessToken(true);
        return;
      }
      submissionData.accessToken = tokenValidation.token;

      // Upload pending files before creating submission.
      // If any upload fails, abort the whole submission — nothing is saved.
      const pendingEntries = Object.entries(pendingFilesRef.current).filter(([, files]) => files.length > 0);
      if (pendingEntries.length > 0) {
        const uploadFailures = [];
        for (const [questionId, files] of pendingEntries) {
          const uploadedIds = [];
          const uploadedNames = [];
          for (const file of files) {
            try {
              const result = await fileUploadService.uploadSubmissionFile(file, { questionId });
              uploadedIds.push(result.id);
              uploadedNames.push(result.originalName);
            } catch (err) {
              console.warn(`Failed to upload file "${file.name}" for question ${questionId}:`, err);
              uploadFailures.push({ questionId, message: err.message });
            }
          }
          if (uploadedIds.length > 0) {
            const note = submissionData.answers[questionId]?.note || '';
            submissionData.answers[questionId] = { fileIds: uploadedIds, fileNames: uploadedNames, note };
          }
        }

        // Block the submission if any file failed to upload.
        if (uploadFailures.length > 0) {
          setFileErrors(prev => {
            const next = { ...prev };
            uploadFailures.forEach(({ questionId, message }) => { next[questionId] = message; });
            return next;
          });
          const details = uploadFailures.map(f => `• ${f.message}`).join('\n');
          alert(`Die Bewerbung wurde NICHT abgesendet, weil eine oder mehrere Dateien nicht hochgeladen werden konnten:\n\n${details}\n\nBitte korrigieren Sie die betroffenen Dateien und senden Sie erneut ab.`);
          return;
        }
      }

      const submissionId = await dbService.createSubmission(submissionData);

      // Console log for successful submission
      console.log('✅ SUBMISSION SUCCESS LOG - Data Saved:', {
        submissionId: submissionId,
        questionnaireId: questionnaireId,
        applicantName: applicantName,
        submissionData: submissionData,
        timestamp: new Date().toISOString()
      });

      // Input log has been submitted in the answer_log field
      
      // Clear stored form data after successful submission
      clearFormData();
      
      setSubmitted(true); 
      
      // Update URL to reflect submitted state
      markSubmittedInUrl();
    } catch (error) {
      const duplicateError = Boolean(
        error?.data?.data?.access_token
        || error?.data?.data?.questionnaire
        || String(error?.message || '').toLowerCase().includes('duplicate')
      );
      if (duplicateError) {
        setSubmitted(true);
        markSubmittedInUrl();
        return;
      }
      // Console log for failed submission with detailed error information
      console.log('❌ SUBMISSION ERROR LOG - Failed to Save:', {
        error: error,
        errorMessage: error.message,
        errorStatus: error.status,
        errorData: error.data,
        errorResponse: error.response,
        questionnaireId: questionnaireId,
        applicantName: applicantName,
        answers: answers,
        submissionData: submissionData,
        timestamp: new Date().toISOString()
      });

      // Input log was included in submission attempt but failed to save
      
      console.error("Error saving submission:", error);
      
      // Provide more specific error messages
      let errorMessage = 'Fehler beim Speichern der Antworten.';
      
      // Check for PocketBase validation errors
      if (error.data) {
        const validationErrors = [];
        if (error.data.data) {
          // PocketBase validation errors are in error.data.data
          Object.keys(error.data.data).forEach(field => {
            const fieldError = error.data.data[field];
            if (fieldError && fieldError.message) {
              validationErrors.push(`${field}: ${fieldError.message}`);
            } else if (fieldError && typeof fieldError === 'object') {
              validationErrors.push(`${field}: ${JSON.stringify(fieldError)}`);
            }
          });
        }
        if (validationErrors.length > 0) {
          errorMessage += `\n\nValidierungsfehler:\n${validationErrors.join('\n')}`;
        }
      }
      
      if (error.message) {
        if (error.message.includes('not authenticated')) {
          errorMessage += ' Authentifizierungsfehler - bitte kontaktieren Sie den Administrator.';
        } else if (error.message.includes('network')) {
          errorMessage += ' Netzwerkfehler - bitte überprüfen Sie Ihre Internetverbindung.';
        } else if (error.message.includes('permission')) {
          errorMessage += ' Berechtigungsfehler - bitte kontaktieren Sie den Administrator.';
        } else if (!error.data || !error.data.data) {
          errorMessage += ` Fehler: ${error.message}`;
        }
      }
      
      alert(errorMessage + '\n\nBitte versuchen Sie es erneut oder kontaktieren Sie den Administrator.');
    } finally {
      // Reset submission flag
      isSubmittingRef.current = false;
    }
  };


  // Helper function to get display text for unanswered questions
  const getUnansweredText = (questionType) => {
    return 'Keine Antwort';
  };

  // Helper function to look up a question's display label from a precomputed label map
  const getQuestionNumber = (question, labelMap) => {
    if (!question) return '?';
    return labelMap[question.id] || '?';
  };

  // Helper function to render a question
  const renderQuestion = (q, index, isReadOnly = false, isCustomQuestion = false, labelMap = {}) => {
    // Safety check - if question is undefined, don't render anything
    if (!q) return null;

    return (
      <div key={q.id} className="scroll-mt-20">
        <div className="flex items-baseline flex-wrap gap-2 mb-1">
          <label className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100" dangerouslySetInnerHTML={{ __html: `${getQuestionNumber(q, labelMap)}. ${sanitizeRichText(q.text)}` }} />
          {q.required === false && !isReadOnly && (
            <span className="text-xs text-slate-500 dark:text-slate-400 font-normal italic">(Freiwillig)</span>
          )}
        </div>
      {q.description && <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-3" dangerouslySetInnerHTML={{ __html: sanitizeRichText(q.description) }} />}
      
      {q.type === 'text' && (
        isReadOnly ? (
          <div className={`w-full p-3 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-lg text-base ${q.id === 'system_name' ? '' : 'min-h-[60px]'}`}
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}>
            {isQuestionAnswered(q.id, q.type, null, q.options) ? answers[q.id] : getUnansweredText(q.type)}
          </div>
        ) : (
          <div className="space-y-2">
            {q.id === 'system_name' ? (
              <input
                key={q.id}
                type="text"
                name={`answer-${q.id}`}
                autoComplete="off"
                required={q.required !== false}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Dein vollständiger Name"
              />
            ) : (
              <textarea
                key={q.id}
                name={`answer-${q.id}`}
                autoComplete="off"
                rows="5"
                required={q.required !== false}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none overflow-hidden"
                style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
              />
            )}
            {q.wordLimit && q.wordLimit !== 'no-limit' && (
                              <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400">
                  <span>Zeichenlimit: {q.wordLimit}</span>
                  <span className={parseInt(q.wordLimit) - (getCharacterCount(answers[q.id] || '') || 0) <= 2 ? 'text-red-500' : 'text-slate-500'}>
                    {getCharacterCount(answers[q.id] || '')} / {q.wordLimit}
                  </span>
                </div>
            )}
          </div>
        )
      )}
      
      {q.type === 'single-choice' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm font-medium text-green-700 dark:text-green-400">Einzelauswahl - Bitte wählen Sie eine Option</span>
          </div>
          {isReadOnly ? (
            <div className="p-3 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <span className="text-slate-700 dark:text-slate-200 text-base">
                {isQuestionAnswered(q.id, q.type, null, q.options) ? answers[q.id] : getUnansweredText(q.type)}
              </span>
            </div>
          ) : (
            q.options.map((opt, i) => (
              <label key={i} className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer gap-3">
                                 <input type="radio" name={`single-choice-${q.id}`} value={opt} checked={answers[q.id] === opt} onChange={(e) => handleAnswerChange(q.id, e.target.value)} className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0"/>
                <span className="text-slate-700 dark:text-slate-200 text-base">{opt}</span>
              </label>
            ))
          )}
        </div>
      )}
      
      {q.type === 'multiple-choice' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-green-600 font-medium text-sm">...</span>
            </div>
            <span className="text-sm font-medium text-green-700 dark:text-green-400">Mehrfachauswahl - Bitte wählen Sie eine oder mehrere Optionen</span>
          </div>
          {isReadOnly ? (
            <div className={`p-3 border rounded-lg ${
              isQuestionAnswered(q.id, q.type, null, q.options)
                ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
            }`}
            style={{
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}>
              <span className={`text-base ${
                isQuestionAnswered(q.id, q.type, null, q.options)
                  ? 'text-slate-700 dark:text-slate-200'
                  : 'text-red-700 dark:text-red-300'
              }`}>
                {isQuestionAnswered(q.id, q.type, null, q.options) ? (answers[q.id] || []).join(', ') : getUnansweredText(q.type)}
              </span>
            </div>
          ) : (
            <div>
              {/* Show selection limit info */}
              {q.maxSelections && (
                <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Wähle bis zu {q.maxSelections} {q.maxSelections === 1 ? 'Antwort' : 'Antworten'} aus
                    {q.maxSelections > 1 && (
                      <span className="block text-xs text-blue-600 dark:text-blue-400 mt-1">
                        ({answers[q.id]?.length || 0} von {q.maxSelections} ausgewählt)
                      </span>
                    )}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                {q.options.map((opt, i) => {
                  const isSelected = (answers[q.id] || []).includes(opt);
                  const isLimitReached = q.maxSelections && !isSelected && (answers[q.id] || []).length >= q.maxSelections;
                  
                  return (
                    <label 
                      key={i} 
                      className={`flex items-center p-3 border rounded-lg gap-3 transition-all duration-200 ${
                        isLimitReached 
                          ? 'border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 cursor-not-allowed opacity-60' 
                          : 'border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
                      }`}
                    >
                      <input 
                        type="checkbox" 
                        value={opt} 
                        checked={isSelected} 
                        onChange={() => handleMultiChoiceChange(q.id, opt)} 
                        disabled={isLimitReached}
                        className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0 disabled:opacity-50"
                      />
                      <span className={`text-base ${
                        isLimitReached 
                          ? 'text-slate-400 dark:text-slate-500' 
                          : 'text-slate-700 dark:text-slate-200'
                      }`}>
                        {opt}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      
      {q.type === 'combobox' && (() => {
        const isPredefined = (q.comboboxMode || 'predefined') === 'predefined';
        const allowAdditionalEntries = Boolean(q.comboboxAllowAdditionalEntries);
        const predefinedEntries = (q.predefinedEntries || []).filter((entry) => entry && entry.trim());
        const currentRows = (answers[q.id] || []).length > 0 ? answers[q.id] : (isPredefined ? [] : [{ label: '', value: '' }]);
        const predefinedRows = predefinedEntries.map((label) => {
          const existing = currentRows.find((row) => row.label === label);
          return existing || { label, value: '' };
        });
        const additionalRows = isPredefined && allowAdditionalEntries
          ? currentRows.filter((row) => !predefinedEntries.includes(row.label))
          : [];
        const rows = isPredefined ? [...predefinedRows, ...additionalRows] : currentRows;
        const suggestions = (q.suggestions || []).filter(s => s && s.trim());
        const inputLabel = q.rowInputLabel || '';
        const dropdownLabel = q.columnLabel || '';

        return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-green-700 dark:text-green-400">Combobox - Einträge hinzufügen und zuordnen</span>
          </div>
          {isReadOnly ? (
            <div className={`p-3 border rounded-lg ${
              isQuestionAnswered(q.id, q.type, null, q.options)
                ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
            }`}>
              {isQuestionAnswered(q.id, q.type, null, q.options) ? (
                <div className="space-y-1">
                  {rows.map((row, index) => (
                    <div key={index} className="flex items-center gap-2 text-slate-700 dark:text-slate-200 text-base">
                      <span className="font-semibold">{row.label}:</span>
                      <span>{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-base text-red-700 dark:text-red-300">{getUnansweredText(q.type)}</span>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {(inputLabel || dropdownLabel) && (
                <div className="flex items-center gap-3 text-sm font-medium text-slate-600 dark:text-slate-400 px-1">
                  <span className="flex-1">{inputLabel}</span>
                  <span className="flex-1">{dropdownLabel}</span>
                  <span className="w-9"></span>
                </div>
              )}
              {rows.map((row, rowIndex) => {
                const rowIsPredefined = isPredefined && predefinedEntries.includes(row.label);
                const filtered = row.label
                  ? suggestions.filter(s => s.toLowerCase().includes(row.label.toLowerCase()) && s.toLowerCase() !== row.label.toLowerCase())
                  : suggestions;
                const rowKey = row.id || (rowIsPredefined ? `pre-${row.label}` : `idx-${rowIndex}`);
                return (
                <div key={rowKey} className="flex items-start gap-3">
                  {rowIsPredefined ? (
                    <div className="flex-1 p-3 border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-lg text-base">
                      {row.label}
                    </div>
                  ) : (
                    <div
                      className="relative flex-1"
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          const el = e.currentTarget.querySelector('[data-combobox-list]');
                          if (el) el.style.display = 'none';
                        }
                      }}
                    >
                      <input
                        type="text"
                        value={row.label}
                        onChange={(e) => {
                          const newLabel = e.target.value;
                          handleAnswerChange(q.id, (prev) => {
                            const base = Array.isArray(prev) && prev.length > 0 ? prev : rows;
                            if (rowIndex >= base.length) return base;
                            const next = [...base];
                            next[rowIndex] = { ...next[rowIndex], label: newLabel };
                            return next;
                          });
                          const list = e.target.parentElement.querySelector('[data-combobox-list]');
                          if (list) list.style.display = '';
                        }}
                        onFocus={(e) => {
                          const list = e.target.parentElement.querySelector('[data-combobox-list]');
                          if (list) list.style.display = '';
                        }}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        name={`combobox-label-${q.id}-${rowIndex}`}
                        className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder={q.rowPlaceholder || 'Eingabe...'}
                      />
                      {suggestions.length > 0 && (
                        <div
                          data-combobox-list=""
                          style={{ display: 'none' }}
                          className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg"
                        >
                          {filtered.length > 0 ? filtered.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAnswerChange(q.id, (prev) => {
                                  const base = Array.isArray(prev) && prev.length > 0 ? prev : rows;
                                  if (rowIndex >= base.length) return base;
                                  const next = [...base];
                                  next[rowIndex] = { ...next[rowIndex], label: s };
                                  return next;
                                });
                                const list = e.target.closest('[data-combobox-list]');
                                if (list) list.style.display = 'none';
                              }}
                              className="w-full text-left px-3 py-2 text-base text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                              {s}
                            </button>
                          )) : (
                            <div className="px-3 py-2 text-base text-slate-400 dark:text-slate-500">Keine Treffer</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex-1">
                    <select
                      value={row.value}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        handleAnswerChange(q.id, (prev) => {
                          const base = Array.isArray(prev) && prev.length > 0 ? prev : rows;
                          if (rowIndex >= base.length) return base;
                          const next = [...base];
                          next[rowIndex] = { ...next[rowIndex], value: newValue };
                          return next;
                        });
                      }}
                      className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">{dropdownLabel || 'Bitte wählen...'}</option>
                      {(q.options || []).map((opt, optIndex) => (
                        <option key={optIndex} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  {!rowIsPredefined && (
                    <button
                      type="button"
                      onClick={() => {
                        handleAnswerChange(q.id, (prev) => {
                          const base = Array.isArray(prev) && prev.length > 0 ? prev : rows;
                          const filteredRows = base.filter((_, i) => i !== rowIndex);
                          return filteredRows.length > 0 ? filteredRows : [{ label: '', value: '', id: makeRowId() }];
                        });
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-full shrink-0 mt-1"
                      title="Eintrag entfernen"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
                );
              })}
              {(!isPredefined || allowAdditionalEntries) && (
                <button
                  type="button"
                  onClick={() => {
                    const newRow = { label: '', value: '', id: makeRowId() };
                    handleAnswerChange(q.id, (prev) => {
                      const base = Array.isArray(prev) && prev.length > 0 ? prev : rows;
                      return [...base, newRow];
                    });
                  }}
                  className="px-3 py-2 text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors inline-flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
                  {inputLabel ? `${inputLabel} hinzufügen` : 'Hinzufügen'}
                </button>
              )}
            </div>
          )}
        </div>
        );
      })()}

      {q.type === 'matrix' && (() => {
        const isPredefined = (q.matrixMode || 'predefined') === 'predefined';
        const allowAdditionalRows = Boolean(q.matrixAllowAdditionalRows);
        const predefinedLabels = (q.rowLabels || []).filter(l => l && l.trim());
        // For predefined mode, ensure answer rows match the defined labels
        const currentRows = (answers[q.id] || []).length > 0 ? answers[q.id] : (isPredefined ? [] : [{ label: '', value: '' }]);
        const predefinedRows = predefinedLabels.map((label) => {
          const existing = currentRows.find((row) => row.label === label);
          return existing || { label, value: '' };
        });
        const additionalRows = isPredefined && allowAdditionalRows
          ? currentRows.filter((row) => !predefinedLabels.includes(row.label))
          : [];
        const effectiveRows = isPredefined ? [...predefinedRows, ...additionalRows] : currentRows;

        const isSingleMode = (q.matrixSelectionMode || 'per-row') === 'single';
        const setRowValue = (rowIndex, nextValue) => {
          handleAnswerChange(q.id, (prev) => {
            const base = Array.isArray(prev) && prev.length > 0 ? prev : effectiveRows;
            if (rowIndex >= base.length) return base;
            if (isSingleMode) {
              return base.map((r, i) =>
                i === rowIndex
                  ? { ...r, value: nextValue }
                  : { ...r, value: '' }
              );
            }
            const next = [...base];
            next[rowIndex] = { ...next[rowIndex], value: nextValue };
            return next;
          });
        };
        const setRowLabel = (rowIndex, newLabel) => {
          handleAnswerChange(q.id, (prev) => {
            const base = Array.isArray(prev) && prev.length > 0 ? prev : effectiveRows;
            if (rowIndex >= base.length) return base;
            const next = [...base];
            next[rowIndex] = { ...next[rowIndex], label: newLabel };
            return next;
          });
        };
        const removeRow = (rowIndex) => {
          handleAnswerChange(q.id, (prev) => {
            const base = Array.isArray(prev) && prev.length > 0 ? prev : effectiveRows;
            return base.filter((_, i) => i !== rowIndex);
          });
        };
        const addRow = () => {
          const newRow = { label: '', value: '', id: makeRowId() };
          handleAnswerChange(q.id, (prev) => {
            const base = Array.isArray(prev) && prev.length > 0 ? prev : effectiveRows;
            return [...base, newRow];
          });
        };
        const renderRadio = (row, rowIndex, opt) => (
          <input
            type="radio"
            name={isSingleMode ? `matrix-${q.id}` : `matrix-${q.id}-${rowIndex}`}
            value={opt}
            checked={row.value === opt}
            onClick={(e) => {
              // Click on already-selected radio = deselect
              if (row.value === opt) {
                e.preventDefault();
                setRowValue(rowIndex, '');
              }
            }}
            onChange={() => setRowValue(rowIndex, opt)}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 cursor-pointer shrink-0"
            title="Klicken zum Auswählen, erneut klicken zum Abwählen"
          />
        );
        const renderLabelInput = (row, rowIndex) => (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={row.label}
              onChange={(e) => setRowLabel(rowIndex, e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name={`matrix-label-${q.id}-${rowIndex}`}
              className={`w-full p-2 border bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                row.value && !row.label
                  ? 'border-red-400 dark:border-red-600'
                  : 'border-slate-300 dark:border-slate-700'
              }`}
              placeholder={q.rowPlaceholder || 'Eintrag'}
            />
            {row.value && !row.label && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Bitte beschriften, damit Ihre Auswahl zugeordnet werden kann.</p>
            )}
          </div>
        );
        const addRowButton = (
          <button
            type="button"
            onClick={addRow}
            className="px-3 py-2 text-sm font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
            Zeile hinzufügen
          </button>
        );
        const removeRowButton = (rowIndex) => (
          <button
            type="button"
            onClick={() => removeRow(rowIndex)}
            className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-full transition-colors inline-flex items-center justify-center shrink-0"
            title="Zeile entfernen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        );

        return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-4 h-4 border-2 border-purple-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
            <span className="text-sm font-medium text-purple-700 dark:text-purple-400">
              {(q.matrixSelectionMode || 'per-row') === 'single'
                ? 'Matrix - Nur eine Auswahl insgesamt (erneut klicken zum Abwählen)'
                : (isPredefined ? 'Matrix - Bitte wählen Sie je eine Option pro Zeile (erneut klicken zum Abwählen)' : 'Matrix - Zeilen hinzufügen und je eine Spaltenoption wählen (erneut klicken zum Abwählen)')}
            </span>
          </div>
          {isReadOnly ? (
            <div className={`p-3 border rounded-lg ${
              isQuestionAnswered(q.id, q.type, null, q.options)
                ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
            }`}>
              {isQuestionAnswered(q.id, q.type, null, q.options) ? (
                <div className="space-y-1">
                  {(answers[q.id] || []).map((row, index) => (
                    <div key={index} className="flex items-center gap-2 text-slate-700 dark:text-slate-200 text-base">
                      <span className="font-semibold">{row.label}:</span>
                      <span>{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-base text-red-700 dark:text-red-300">{getUnansweredText(q.type)}</span>
              )}
            </div>
          ) : isMobileViewport ? (
            <div className="space-y-3">
              {effectiveRows.map((row, rowIndex) => {
                const rowKey = row.id || (isPredefined && predefinedLabels.includes(row.label) ? `pre-${row.label}` : `idx-${rowIndex}`);
                const isPredefinedRow = isPredefined && predefinedLabels.includes(row.label);
                return (
                  <div key={rowKey} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      {isPredefinedRow ? (
                        <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word flex-1 min-w-0">
                          {row.label}
                        </span>
                      ) : (
                        renderLabelInput(row, rowIndex)
                      )}
                      {!isPredefinedRow && removeRowButton(rowIndex)}
                    </div>
                    <div>
                      {(q.options || []).map((opt, optIndex) => (
                        <label key={optIndex} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          {renderRadio(row, rowIndex, opt)}
                          <span className="text-sm text-slate-700 dark:text-slate-200 wrap-break-word min-w-0">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              {(!isPredefined || allowAdditionalRows) && (
                <div className="mt-4">{addRowButton}</div>
              )}
            </div>
          ) : (
            <div className="w-full" style={{ overflowX: 'auto' }}>
              <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="p-2 font-medium text-sm text-slate-600 dark:text-slate-400" style={{ width: '33%', minWidth: '150px' }}></th>
                    {(q.options || []).map((opt, optIndex) => (
                      <th key={optIndex} className="p-2 font-medium text-sm text-slate-600 dark:text-slate-400 text-center" style={{ minWidth: '80px' }}>
                        {opt}
                      </th>
                    ))}
                    {(!isPredefined || allowAdditionalRows) && <th className="p-2 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {effectiveRows.map((row, rowIndex) => {
                    const rowKey = row.id || (isPredefined && predefinedLabels.includes(row.label) ? `pre-${row.label}` : `idx-${rowIndex}`);
                    return (
                    <tr key={rowKey} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="p-2">
                        {isPredefined && predefinedLabels.includes(row.label) ? (
                          <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                            {row.label}
                          </span>
                        ) : (
                          renderLabelInput(row, rowIndex)
                        )}
                      </td>
                      {(q.options || []).map((opt, optIndex) => (
                        <td key={optIndex} className="p-2 text-center">
                          {renderRadio(row, rowIndex, opt)}
                        </td>
                      ))}
                      {(!isPredefined || !predefinedLabels.includes(row.label)) && (
                        <td className="p-2 text-right">
                          {removeRowButton(rowIndex)}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!isPredefined || allowAdditionalRows) && (
                <div className="mt-4">{addRowButton}</div>
              )}
            </div>
          )}
        </div>
        );
      })()}

      {q.type === 'ranking' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xs">1</span>
              </div>
              <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xs">2</span>
              </div>
              <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                <span className="text-blue-600 font-bold text-xs">3</span>
              </div>
              <span className="text-blue-600 font-medium text-sm">...</span>
            </div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Rangordnung - 1 = Am wichtigsten, {q.options.length} = Am wenigsten wichtig</span>
          </div>
          {isReadOnly ? (
            <div className={`p-3 border rounded-lg ${
              isQuestionAnswered(q.id, q.type, null, q.options)
                ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
            }`}>
              {isQuestionAnswered(q.id, q.type, null, q.options) ? (
                <div className="space-y-1">
                  {(answers[q.id] || []).map((item, index) => (
                    <div key={index} className="text-slate-700 dark:text-slate-200 text-base">
                      <span className="font-semibold">{index + 1}.</span> {item}
                    </div>
                  ))}
                </div>
              ) : (
                <span className={`text-base ${
                  isQuestionAnswered(q.id, q.type, null, q.options)
                    ? 'text-slate-700 dark:text-slate-200'
                    : 'text-red-700 dark:text-red-300'
                }`}>{getUnansweredText(q.type)}</span>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleRankingChange(q.id, opt)}
                    className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 gap-3 w-full text-left transition-colors"
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-sm transition-colors ${
                      (answers[q.id] || []).includes(opt)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500'
                    }`}>
                      {(answers[q.id] || []).indexOf(opt) + 1 || ''}
                    </div>
                    <span className="text-slate-700 dark:text-slate-200 text-base flex-1">{opt}</span>
                  </button>
                ))}
              </div>
              {(answers[q.id] || []).length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Ihre Reihenfolge:</strong>
                  </p>
                  <div className="mt-2 space-y-1">
                    {(answers[q.id] || []).map((item, index) => (
                      <div key={index} className="text-sm text-blue-700 dark:text-blue-300">
                        <span className="font-semibold">{index + 1}.</span> {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {q.type === 'image' && (
        <div className="space-y-4">
          {(q.imageUrl || q.imageDataUrl) && (
            <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-600">
              <img 
                src={q.imageUrl || q.imageDataUrl} 
                alt="Fragebild" 
                className="max-h-56 sm:max-h-64 w-auto mx-auto rounded object-contain" 
              />
            </div>
          )}
          {(!q.answerType || q.answerType === 'text') && (
            isReadOnly ? (
              <div className={`w-full p-3 border rounded-lg text-base min-h-[60px] ${
                isQuestionAnswered(q.id, 'text')
                  ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200'
                  : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              }`}
              style={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap'
              }}>
                {isQuestionAnswered(q.id, 'text') ? answers[q.id] : getUnansweredText('text')}
              </div>
            ) : (
              <textarea
                key={q.id}
                name={`answer-${q.id}`}
                autoComplete="off"
                rows="5"
                required={q.required !== false}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none overflow-hidden"
                placeholder="Ihre Antwort"
                style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
              />
            )
          )}
          {q.answerType === 'single-choice' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 rounded-full border-2 border-green-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                </div>
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Einzelauswahl - Bitte wählen Sie eine Option</span>
              </div>
              {isReadOnly ? (
                <div className={`p-3 border rounded-lg ${
                  isQuestionAnswered(q.id, 'single-choice')
                    ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                    : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                }`}>
                  <span className={`text-base ${
                    isQuestionAnswered(q.id, 'single-choice')
                      ? 'text-slate-700 dark:text-slate-200'
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    {isQuestionAnswered(q.id, 'single-choice') ? answers[q.id] : getUnansweredText('single-choice')}
                  </span>
                </div>
              ) : (
                q.options?.map((opt, i) => (
                  <label key={i} className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer gap-3">
                    <input type="radio" name={`single-choice-${q.id}`} value={opt} checked={answers[q.id] === opt} onChange={(e) => handleAnswerChange(q.id, e.target.value)} className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0"/>
                    <span className="text-slate-700 dark:text-slate-200 text-base">{opt}</span>
                  </label>
                ))
              )}
            </div>
          )}
          {q.answerType === 'multiple-choice' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 border-2 border-green-600 rounded flex items-center justify-center">
                  <div className="w-2 h-2 bg-green-600 rounded"></div>
                </div>
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Mehrfachauswahl - Bitte wählen Sie eine oder mehrere Optionen</span>
              </div>
              {isReadOnly ? (
                <div className="p-3 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg"
                style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}>
                  <span className="text-slate-700 dark:text-slate-200 text-base">
                    {isQuestionAnswered(q.id, 'multiple-choice') ? (answers[q.id] || []).join(', ') : getUnansweredText('multiple-choice')}
                  </span>
                </div>
                              ) : (
                  <>
                    {q.options && q.options.length > 0 ? (
                      q.options.map((opt, i) => (
                        <label key={i} className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer gap-3">
                          <input type="checkbox" value={opt} checked={(answers[q.id] || []).includes(opt)} onChange={() => handleMultiChoiceChange(q.id, opt)} className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0"/>
                          <span className="text-slate-700 dark:text-slate-200 text-base">{opt}</span>
                        </label>
                      ))
                    ) : (
                      <div className="p-3 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <span className="text-red-700 dark:text-red-300 text-sm">
                          ⚠️ Keine Antwortoptionen definiert. Bitte kontaktieren Sie den Administrator.
                        </span>
                      </div>
                    )}
                  </>
                )}
            </div>
          )}
        </div>
      )}

      {q.type === 'file-upload' && (
        <div className="space-y-3">
          {/* Drop zone */}
          {!isReadOnly && (
            <label
              className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-green-500 dark:hover:border-green-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer.files || []);
                files.forEach(file => handleFileSelect(q.id, file, q));
              }}
            >
              <svg className="w-8 h-8 text-slate-400 dark:text-slate-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-slate-500 dark:text-slate-400">Datei hierher ziehen oder klicken zum Auswählen</span>
              <input
                type="file"
                multiple
                className="hidden"
                accept={(q.acceptedFileTypes || []).join(',')}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach(file => handleFileSelect(q.id, file, q));
                  e.target.value = '';
                }}
              />
            </label>
          )}

          {/* Error */}
          {fileErrors[q.id] && (
            <div className="p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
              <p className="text-red-600 dark:text-red-400 text-sm">{fileErrors[q.id]}</p>
            </div>
          )}

          {/* Uploaded files list */}
          {(answers[q.id]?.fileNames || []).length > 0 && (
            <div className="space-y-2">
              {answers[q.id].fileNames.map((name, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{name}</span>
                  </div>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => handleFileRemove(q.id, idx)}
                      className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 shrink-0"
                      title="Datei entfernen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Optional note textarea */}
          {!isReadOnly ? (
            <textarea
              key={`file-note-${q.id}`}
              name={`file-note-${q.id}`}
              autoComplete="off"
              rows="2"
              value={answers[q.id]?.note || ''}
              onChange={(e) => handleFileNoteChange(q.id, e.target.value)}
              className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
              placeholder={q.requireNote ? 'Notiz (erforderlich)' : 'Optionale Notiz'}
            />
          ) : answers[q.id]?.note ? (
            <div className="p-3 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-700 dark:text-slate-200">
              {answers[q.id].note}
            </div>
          ) : null}

          {/* Hints */}
          <div className="text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
            {(q.acceptedFileTypes || []).length > 0 && (
              <p>Erlaubte Dateitypen: {(q.acceptedFileTypes || []).map(t => {
                const map = { 'application/pdf': 'PDF', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX', 'image/png': 'PNG', 'image/jpeg': 'JPG', 'image/webp': 'WebP' };
                return map[t] || t;
              }).join(', ')}</p>
            )}
            <p>Max. Dateigröße: {q.maxFileSize || 10} MB</p>
          </div>
        </div>
      )}
    </div>
  );
  }; // renderQuestion function ends here
  
  if (isQuestionnaireLoading) {
    return (
      <div className={`${theme === 'dark' ? 'bg-slate-900' : 'bg-white'} min-h-screen flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
          <p className={`text-base font-semibold ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
            Fragebogen wird geladen...
          </p>
          <svg
            className={`h-8 w-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className={`${theme === 'dark' ? 'bg-slate-900' : 'bg-white'} min-h-screen flex items-center justify-center`}>
        <div className="text-center p-8">
          <div className="font-bold text-red-600 mb-2">Fragebogen nicht gefunden.</div>
          <div className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Bitte überprüfen Sie den Link und versuchen Sie es erneut.
          </div>
        </div>
      </div>
    );
  }

  if (invalidAccessToken) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-slate-900">
        <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
        <div className="text-center bg-white dark:bg-slate-800 dark:text-slate-200 p-10 rounded-2xl shadow-lg max-w-lg mx-4">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-amber-600 dark:text-amber-400 mb-3">Link ungültig oder abgelaufen</h1>
          <p className="text-slate-600 dark:text-slate-300">
            Dieser Fragebogen-Link kann nicht mehr verwendet werden. Bitte fordern Sie einen neuen Link an.
          </p>
        </div>
      </div>
    );
  }

  // Show withdrawal confirmation if user has withdrawn
  if (isWithdrawn) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-slate-900">
        <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
        <div className="text-center bg-white dark:bg-slate-800 dark:text-slate-200 p-10 rounded-2xl shadow-lg max-w-lg mx-4">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-red-600 dark:text-red-400 mb-4">Bewerbung zurückgezogen</h1>
          <div className="text-slate-600 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: questionnaire?.cancelOutroText || 'Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich abgesagt. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen.' }} />
        </div>
      </div>
    );
  }
  
  if (submitted) return (
    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-slate-900">
      <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
      <div className="text-center bg-white dark:bg-slate-800 dark:text-slate-200 p-10 rounded-2xl shadow-lg max-w-lg mx-4">
        {!showNextSteps ? (
          <>
            <h1 className="text-3xl font-bold text-green-600">Vielen Dank!</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-300">Ihre Antworten wurden erfolgreich übermittelt.</p>
            <ModernButton onClick={() => setShowNextSteps(true)} variant="success" size="lg" className="mt-8">
              Wie geht es nun weiter?
            </ModernButton>
          </>
        ) : (
          <div className="text-left">
            <h1 className="text-3xl font-bold bg-linear-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">Die nächsten Schritte</h1>
            <p className="mt-4 text-slate-600 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: sanitizeRichText(questionnaire.outroText || 'Vielen Dank für Ihre Zeit und die sorgfältigen Antworten. Wir haben Ihre Unterlagen erhalten und werden sie nun intern prüfen.<br/><br/>Wir bemühen uns, Ihnen so schnell wie möglich eine Rückmeldung zu geben und werden Sie, wie vereinbart, in Kürze kontaktieren, um Sie über den weiteren Verlauf des Bewerbungsprozesses zu informieren.') }}></p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 min-h-screen font-sans py-4 sm:py-12">
      <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
      
      {/* Timer Display - Only show when auto-submit is enabled */}
      {timerStarted && currentStep !== 'intro' && (questionnaire?.autoSubmitEnabled === true || questionnaire?.intro_page?.autoSubmitEnabled === true) && (
        <div className="fixed top-4 right-20 z-50 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg">
          <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-red-700 dark:text-red-300 font-semibold text-sm">
            {(() => {
              const minutes = Math.floor(timeLeft / 60);
              const seconds = timeLeft % 60;
              return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            })()}
          </span>
        </div>
      )}
      
      <div className="container mx-auto p-4 max-w-3xl">
        <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs text-slate-800 dark:text-slate-200 p-4 sm:p-6 md:p-10 rounded-2xl shadow-xl border border-slate-200/50 dark:border-slate-700/50">
            <header className="mb-6 sm:mb-8 text-center">
              <h1 className="text-2xl sm:text-4xl font-bold bg-linear-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent wrap-break-word">{questionnaire.title}</h1>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-2" dangerouslySetInnerHTML={{ __html: sanitizeRichText(questionnaire.introText || 'Bitte füllen Sie die folgenden Felder aus.') }}></p>
            </header>

            {/* Step indicator */}
            <div className="mb-6 flex items-center justify-center">
              <div className="w-full max-w-2xl px-2">
                <div className="flex items-center">
                  {stepperItems.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        index <= activeStepIndex
                          ? 'bg-green-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                      }`}>
                        {index + 1}
                      </div>
                      {index < stepperItems.length - 1 && (
                        <div className={`h-1 flex-1 mx-2 ${
                          index < activeStepIndex ? 'bg-green-600' : 'bg-slate-200 dark:bg-slate-700'
                        }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

                        {/* Step labels */}
            <div className="mb-6 text-center text-lg sm:text-xl font-semibold text-slate-700 dark:text-slate-300">
              <div className="transition-all duration-500 ease-in-out transform">
                <div className="animate-fadeIn">{currentStepLabel}</div>
                {currentStepDescription && (
                  <p
                    className="mt-2 text-sm sm:text-base font-normal text-slate-600 dark:text-slate-400"
                    dangerouslySetInnerHTML={{ __html: sanitizeRichText(currentStepDescription) }}
                  />
                )}
              </div>
            </div>

                        {currentStep === 'intro' ? (
              // Introduction step
              <div className="space-y-6 sm:space-y-8 animate-slideIn">
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                  <div className="space-y-4 text-blue-700 dark:text-blue-300">
                    <p>
                      {questionnaire.intro_page?.welcomeText || questionnaire.introWelcomeText || 'Dieser Fragebogen besteht aus mehreren Teilen:'}
                    </p>
                    {String(introOverviewText || '').trim() && (
                      <p dangerouslySetInnerHTML={{ __html: sanitizeRichText(introOverviewText) }} />
                    )}
                    <ul className="space-y-3">
                      {introSectionItems.map((section, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400 shrink-0" />
                          <div className="min-w-0">
                            <div className="font-semibold" dangerouslySetInnerHTML={{ __html: sanitizeRichText(section.title) }} />
                            {String(section.description || '').trim() && (
                              <div className="text-sm mt-1 opacity-90" dangerouslySetInnerHTML={{ __html: sanitizeRichText(section.description) }} />
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {String(introAfterSectionsText || '').trim() && (
                      <p dangerouslySetInnerHTML={{ __html: sanitizeRichText(introAfterSectionsText) }} />
                    )}
                  </div>
                </div>

                {((questionnaire?.autoSubmitEnabled === true || questionnaire?.intro_page?.autoSubmitEnabled === true) && (questionnaire.intro_page?.timerText || questionnaire.introTimerText || 'Sie haben 15 Minuten Zeit, um den Fragebogen zu vervollständigen. Nach Ablauf der Zeit wird der Fragebogen automatisch eingereicht, auch wenn nicht alle Fragen beantwortet wurden.') && (questionnaire.intro_page?.timerText || questionnaire.introTimerText || 'Sie haben 15 Minuten Zeit, um den Fragebogen zu vervollständigen. Nach Ablauf der Zeit wird der Fragebogen automatisch eingereicht, auch wenn nicht alle Fragen beantwortet wurden.').trim()) && (
                  <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                      <span className="font-semibold text-amber-800 dark:text-amber-200">Wichtiger Hinweis</span>
                            </div>
                    <p className="text-amber-700 dark:text-amber-300" dangerouslySetInnerHTML={{ __html: sanitizeRichText(questionnaire.intro_page?.timerText || questionnaire.introTimerText || 'Sie haben 15 Minuten Zeit, um den Fragebogen zu vervollständigen. Nach Ablauf der Zeit wird der Fragebogen automatisch eingereicht, auch wenn nicht alle Fragen beantwortet wurden.') }} />
                          </div>
                )}

                {/* Datenschutz Consent */}
                <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                  <div>
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={datenschutzConsent}
                        onChange={(e) => {
                          const newConsent = e.target.checked;
                          setDatenschutzConsent(newConsent);
                          setShowDatenschutzError(false); // Hide error when user checks the box
                          saveFormData(undefined, undefined, undefined, newConsent);
                        }}
                        className={`mt-1 w-4 h-4 text-blue-600 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-500 ${
                          showDatenschutzError && !datenschutzConsent ? 'border-red-500' : ''
                        }`}
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                        <a 
                          href="https://ei4heidelberg.de/datenschutz"
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                        >
                          Datenschutzerklärung
                        </a>{' '}
                        zu. *
                      </span>
                    </label>
                    {showDatenschutzError && !datenschutzConsent && (
                      <p className="mt-1 text-sm text-red-600">Bitte stimmen Sie der Datenschutzerklärung zu, um den Fragebogen zu starten.</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-center">
                  <ModernButton 
                    onClick={startTimer} 
                    variant="success" 
                    size="xl"
                    className="px-8 py-4 text-lg"
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  >
                    {questionnaire.intro_page?.startButtonText || questionnaire.introStartButtonText || 'Starten'}
                  </ModernButton>
                      </div>
                            </div>
                        ) : showConfirmForm ? (
              // Confirmation view
              <div className="space-y-6 sm:space-y-8 animate-scaleIn">
                {/* Final Submission Header */}
                <div className="text-center space-y-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-6">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-green-800 dark:text-green-200">
                    Zusammenfassung
                  </h2>
                  <p className="text-green-700 dark:text-green-300">
                    Bitte überprüfen Sie nochmal alle Ihre Antworten, bevor Sie den Fragebogen absenden.
                  </p>
                </div>

                {questionFlowItems.map((item, index) => (
                  <div key={`summary_${item.id}_${index}`} className="space-y-6">
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200 border-b-2 border-slate-300 dark:border-slate-600 pb-3 flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${item.type === 'default' ? 'bg-blue-600' : 'bg-green-600'}`}>
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      {item.label}
                    </h3>
                    {item.type === 'default'
                      ? (() => {
                        const allDefaultQuestions = questionnaire?.defaultQuestions || [];
                        const defaultLabelMap = computeDisplayLabels(allDefaultQuestions, 0);
                        const visibleDefaultQuestions = allDefaultQuestions
                          .filter((question) => shouldShowConditionalQuestion(question, answers));
                        return visibleDefaultQuestions.map((q, questionIndex) => renderQuestion(q, questionIndex, true, false, defaultLabelMap));
                      })()
                      : (() => {
                        const allCustomQuestions = item.page?.questions || [];
                        const customLabelMap = computeDisplayLabels(allCustomQuestions);
                        const visibleCustomQuestions = allCustomQuestions
                          .filter((question) => shouldShowConditionalQuestion(question, answers));
                        return visibleCustomQuestions.map((q, questionIndex) => renderQuestion(q, questionIndex, true, true, customLabelMap));
                      })()}
                  </div>
                ))}

                                {/* Final Action Section */}
                <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                  <div className="text-center mb-4">
                    <p className="text-slate-700 dark:text-slate-300 font-medium">
                      Sind Sie mit allen Antworten zufrieden?
                    </p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <ModernButton 
                      onClick={goBackToCustom} 
                      variant="secondary" 
                      size="lg"
                    >
                      Zurück bearbeiten
                    </ModernButton>
                    <button
                      onClick={handleWithdrawalRequest}
                      className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors border border-red-600 shadow-lg hover:shadow-xl"
                    >
                      Bewerbung zurückziehen
                    </button>
                    <ModernButton 
                      onClick={handleSubmit} 
                      variant="success" 
                      size="xl"
                      className="px-8 py-4 text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                      icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
                    >
                      Jetzt absenden ✓
                    </ModernButton>
                  </div>
                </div>
                        </div>
            ) : (
              // Form view
              <form
                key={`form-${currentStep}-${customPageIndex}`}
                autoComplete="off"
                onSubmit={(e) => {
                e.preventDefault();
                if (currentStep === 'default') {
                  goToCustomQuestions();
                } else if (currentStep === 'custom') {
                  goToNextCustomPage();
                }
              }} className="space-y-6 sm:space-y-8 animate-slideIn">
                {currentStep === 'default' && questionnaire && (
                  // Default questions (including system_name as first editable question)
                  <div className="space-y-6">
                    {(() => {
                      const allQuestions = questionnaire?.defaultQuestions || [];
                      const defaultLabelMap = computeDisplayLabels(allQuestions, 0);
                      const visibleQuestions = allQuestions.filter((question) => shouldShowConditionalQuestion(question, answers));
                      return visibleQuestions.map((q, index) => renderQuestion(q, index, false, false, defaultLabelMap));
                    })()}
                      </div>
                    )}


                {currentStep === 'custom' && (
                  // Custom questions
                  <div className="space-y-6">
                    {(() => {
                      const allQuestions = currentCustomPage?.questions || [];
                      const customLabelMap = computeDisplayLabels(allQuestions);
                      const visibleQuestions = allQuestions.filter((question) => shouldShowConditionalQuestion(question, answers));
                      return visibleQuestions.map((q, index) => renderQuestion(q, index, false, true, customLabelMap));
                    })()}
                  </div>
                )}

                <div className="h-28 sm:h-0" />
                <div className="fixed bottom-0 left-0 right-0 sm:static bg-white dark:bg-slate-800 sm:bg-transparent border-t border-slate-200 dark:border-slate-700 sm:border-0 p-4 sm:p-0 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] sm:shadow-none pb-safe">
                  <div className="max-w-3xl mx-auto text-center">
                    <div className="flex gap-4 justify-center">
                      {currentStep === 'default' && currentQuestionFlowIndex > 0 && (
                        <ModernButton
                          onClick={goBackFromDefault}
                          variant="secondary"
                          size="lg"
                        >
                          Zurück
                        </ModernButton>
                      )}
                      {currentStep === 'custom' && currentQuestionFlowIndex > 0 && (
                        <ModernButton
                          onClick={goBackWithinCustom}
                          variant="secondary"
                          size="lg"
                        >
                          Zurück
                        </ModernButton>
                      )}
                    <ModernButton 
                      type="submit" 
                      variant="success" 
                      size="xl" 
                      className="w-full sm:w-auto"
                        icon={!isLastQuestionFlowPage ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                        )}
                      >
                        {!isLastQuestionFlowPage ? 'Weiter' : 'Überprüfen'}
                    </ModernButton>
                    </div>
                  </div>
                </div>
            </form>
            )}
        </div>
      </div>
      
      {/* Footer */}
      <footer className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex justify-center items-center text-xs text-slate-500 dark:text-slate-400">
          <div className="flex space-x-4">
            <a
              href="https://ei4heidelberg.de/impressum"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Impressum
            </a>
            <a
              href="https://ei4heidelberg.de/datenschutz"
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Datenschutz
            </a>
          </div>
        </div>
      </footer>

      {/* Withdrawal Modal */}
      <WithdrawalModal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        onConfirmWithdrawal={handleConfirmWithdrawal}
        onStayInPool={handleStayInPool}
        cancelText={questionnaire?.cancelText}
        cancelOutroText={questionnaire?.cancelOutroText}
      />
      <WithdrawalModal
        isOpen={unansweredModalState.isOpen}
        onClose={closeUnansweredModal}
        onConfirmWithdrawal={closeUnansweredModal}
        title="Bitte vervollständigen Sie den Fragebogen"
        description="Beantworten Sie zuerst alle Pflichtfragen, bevor Sie zur nächsten Seite wechseln."
        confirmLabel="Verstanden"
        showSecondary={false}
        icon="info"
      >
        {unansweredModalState.questions.length > 0 && (
          <div className="mt-4 max-h-52 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-left">
            <ul className="space-y-2">
              {unansweredModalState.questions.map((question, index) => (
                <li key={`${question}_${index}`} className="text-sm text-slate-700 dark:text-slate-200">
                  {question}
                </li>
              ))}
            </ul>
          </div>
        )}
      </WithdrawalModal>
    </div>
  );
}
